<template>
  <div class="profile-page">
    <div class="profile-layout profile-layout--fidelity">
      <section class="profile-card profile-hero-card">
        <div class="profile-hero-grid">
          <div class="profile-hero-identity">
            <div class="profile-avatar-wrap" @click="triggerFileInput">
              <div class="profile-avatar-frame">
                <div class="profile-avatar-shell">
                  <template v-if="avatarUrl">
                    <img :src="avatarUrl" alt="User Avatar" class="profile-avatar-img" />
                  </template>
                  <template v-else>
                    <div class="profile-avatar-fallback">{{ avatarInitials }}</div>
                  </template>
                  <div class="profile-avatar-overlay">
                    <i class="fas fa-camera"></i>
                  </div>
                </div>
              </div>
              <input
                type="file"
                ref="fileInput"
                accept="image/png,image/jpeg,image/webp,image/avif"
                @change="onFileChange"
                hidden
              />
              <button
                v-if="avatarDirty"
                class="profile-avatar-save-btn"
                type="button"
                @click.stop="saveAvatarChange"
                aria-label="Salvar foto de perfil"
              >
                <i class="fas fa-check"></i>
              </button>
              <div class="avatar-level-chip">LVL {{ userLevel }}</div>
            </div>

            <div class="profile-hero-copy">
              <div class="profile-name-row">
                <div class="profile-name-block">
                  <div class="profile-name-line">
                    <template v-if="!editUsername">
                      <h1 class="profile-display-name">{{ username }}</h1>
                    </template>
                    <template v-else>
                      <input
                        type="text"
                        v-model="usernameEdit"
                        class="profile-display-input"
                        @input="handleUsernameInput"
                      />
                    </template>
                    <button
                      v-if="editUsername"
                      class="profile-name-edit-btn"
                      type="button"
                      @click="toggleEditUsername"
                      :aria-label="editActionLabel"
                    >
                      <i class="fas" :class="editActionIcon"></i>
                    </button>
                  </div>
                  <p class="profile-user-handle">{{ userHandle }}</p>
                  <p class="profile-discord-pill" :class="discordConnected ? 'is-connected' : 'is-idle'">
                    <span class="profile-discord-pill__orb">
                      <i class="fab fa-discord"></i>
                    </span>
                    <span class="profile-discord-pill__body">
                      <span class="profile-discord-pill__label">
                        {{ discordConnected ? 'Discord Vinculado' : 'Discord Bloqueado' }}
                      </span>
                      <span class="profile-discord-pill__meta">
                        {{ discordConnected ? (discordDisplayName || 'Conta conectada') : 'Conecte para desbloquear' }}
                      </span>
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div class="profile-hero-side">
            <aside
              class="profile-rank-card profile-rank-card--hero"
              :class="[showTierBadge ? activeTierBadge.className : 'insignia-badge--locked']"
            >
              <div class="profile-rank-card__orb">
                <i class="fas" :class="showTierBadge ? activeTierBadge.icon : 'fa-lock'"></i>
              </div>
              <div class="profile-rank-card__body">
                <span class="profile-rank-card__title">{{ showTierBadge ? activeTierBadge.label : 'Insígnia de Tier' }}</span>
                <div class="profile-rank-card__meta">
                  <span>Nível {{ userLevel }}</span>
                </div>
              </div>
            </aside>

            <section class="profile-hero-badges" aria-label="Insígnias em destaque">
              <div class="profile-badge-list profile-badge-list--hero">
                <article v-if="showBetaTesterBadge" class="insignia-badge profile-badge-card profile-badge-card--beta profile-badge-card--hero">
                  <span class="insignia-badge__orb profile-badge-card__orb">
                    <i class="fas fa-flask"></i>
                  </span>
                  <span class="insignia-badge__body profile-badge-card__body">
                    <span class="insignia-badge__name profile-badge-card__name">BetaTester</span>
                    <span class="insignia-badge__meta profile-badge-card__meta">Acesso antecipado</span>
                  </span>
                </article>
                <article
                  class="insignia-badge profile-badge-card profile-badge-card--discord profile-badge-card--hero"
                  :class="discordConnected ? 'is-unlocked' : 'is-locked'"
                >
                  <span class="insignia-badge__orb profile-badge-card__orb">
                    <i class="fab fa-discord"></i>
                  </span>
                  <span class="insignia-badge__body profile-badge-card__body">
                    <span class="insignia-badge__name profile-badge-card__name">Discord</span>
                    <span class="insignia-badge__meta profile-badge-card__meta">
                      {{ discordConnected ? 'Conta vinculada' : 'Conecte para desbloquear' }}
                    </span>
                  </span>
                </article>
              </div>
            </section>
          </div>
        </div>

        <div class="profile-hero-footer">
          <div class="profile-xp-block">
            <div class="profile-xp-head">
              <div class="profile-xp-copy">
                <div class="stat-number profile-xp-stat">{{ userXP }} / 100 XP</div>
              </div>
              <div class="xp-meta profile-xp-meta">
                Faltam <span class="xp-remaining">{{ 100 - userXP }}</span> XP para o próximo nível.
              </div>
            </div>
            <div class="profile-xp-bar" aria-hidden="true">
              <div class="profile-xp-fill" :style="{ width: userXP + '%' }"></div>
              <div class="profile-xp-marker" :style="{ left: userXP + '%' }"></div>
              <div
                v-for="m in xpMilestones"
                :key="`profile-inline-xp-${m}`"
                v-show="userXP < m"
                class="profile-xp-milestone"
                :style="{ left: m + '%' }"
              >
                <span class="profile-xp-m-dot"></span>
              </div>
            </div>
          </div>

          <div class="profile-actions">
            <button
              class="profile-action-btn profile-action-btn--primary"
              type="button"
              @click="walletConnected ? disconnectBybit() : deposit()"
            >
              <i class="fas fa-wallet"></i>
              {{ walletConnected ? 'DESCONECTAR' : 'CONECTAR' }}
            </button>
            <button
              class="profile-action-btn profile-action-btn--icon"
              type="button"
              @click="handleDiscordConnect"
              :disabled="isDiscordLoading"
              :aria-label="discordConnected ? 'Reconectar Discord' : 'Conectar Discord'"
            >
              <i class="fab" :class="isDiscordLoading ? 'fa-spinner fa-spin' : 'fa-discord'"></i>
            </button>
            <button
              v-if="discordConnected"
              class="profile-action-btn profile-action-btn--icon"
              type="button"
              @click="disconnectDiscord"
              :disabled="isDiscordDisconnecting"
              aria-label="Desconectar Discord"
            >
              <i class="fas" :class="isDiscordDisconnecting ? 'fa-spinner fa-spin' : 'fa-unlink'"></i>
            </button>
            <button
              v-if="!editUsername"
              class="profile-action-btn profile-action-btn--icon"
              type="button"
              @click="toggleEditUsername"
              aria-label="Mudar nome"
            >
              <i class="fas fa-pen"></i>
            </button>
            <button
              class="profile-action-btn profile-action-btn--icon"
              type="button"
              @click="openChangePasswordModal"
              aria-label="Alterar senha"
            >
              <i class="fas fa-key"></i>
            </button>
          </div>
        </div>
      </section>

      <div class="profile-content-grid">
        <main class="profile-main">
          <section class="profile-card profile-dashboard-card">
            <div class="section-heading">
              <div>
                <p class="section-kicker">Desempenho</p>
                <h2 class="profile-dashboard-title">Mini dashboard do player</h2>
              </div>
            </div>

            <div class="metrics-grid profile-metrics-grid">
              <article class="metric-card profile-metric-card" v-for="metric in metrics" :key="metric.label">
                <div class="metric-label profile-metric-label">{{ metric.label }}</div>
                <div class="metric-value profile-metric-value">{{ metric.value }}</div>
              </article>
            </div>

            <div class="profile-pnl-card">
              <div class="profile-pnl-card__header">
                <div>
                  <div class="profile-pnl-label">PNL 30D</div>
                  <div class="profile-pnl-value" :class="pnlValueClass">{{ pnl30d }}</div>
                </div>
                <div class="profile-pnl-note">
                  {{ pnlRangeStartLabel }} - {{ pnlRangeEndLabel }}
                </div>
              </div>
              <div class="profile-pnl-chart" aria-hidden="true">
                <div class="pnl-mini-chart">
                  <div class="pnl-mini-chart__y-axis">
                    <svg class="axis-svg" :viewBox="`0 0 38 ${pnlChartHeight}`" role="presentation">
                      <text
                        v-for="(row, index) in pnlAxisRows"
                        :key="`profile-pnl-axis-${index}`"
                        class="axis-text"
                        x="36"
                        :y="row.y"
                        text-anchor="end"
                        :dominant-baseline="index === 0 ? 'hanging' : index === pnlAxisRows.length - 1 ? 'ideographic' : 'middle'"
                      >
                        {{ row.label }}
                      </text>
                    </svg>
                  </div>
                  <div class="pnl-mini-chart__canvas">
                    <svg :viewBox="`0 0 ${pnlChartWidth} ${pnlChartHeight}`" preserveAspectRatio="none" class="pnl-line-svg">
                      <defs>
                        <linearGradient id="profilePnlAreaPositive" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stop-color="#30c58d" stop-opacity="0.42" />
                          <stop offset="100%" stop-color="#30c58d" stop-opacity="0.05" />
                        </linearGradient>
                        <linearGradient id="profilePnlAreaNegative" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stop-color="#f05465" stop-opacity="0.4" />
                          <stop offset="100%" stop-color="#f05465" stop-opacity="0.08" />
                        </linearGradient>
                        <clipPath id="profilePnlClipTop">
                          <rect :x="0" :y="0" :width="pnlChartWidth" :height="pnlZeroY" />
                        </clipPath>
                        <clipPath id="profilePnlClipBottom">
                          <rect :x="0" :y="pnlZeroY" :width="pnlChartWidth" :height="pnlChartHeight - pnlZeroY" />
                        </clipPath>
                      </defs>

                      <line
                        v-for="(row, idx) in pnlGridRows"
                        :key="`profile-pnl-grid-${idx}`"
                        x1="0"
                        :y1="row.y"
                        :x2="pnlChartWidth"
                        :y2="row.y"
                        class="pnl-grid-line"
                      />

                      <line x1="0" :y1="pnlZeroY" :x2="pnlChartWidth" :y2="pnlZeroY" class="pnl-grid-zero" />

                      <path class="pnl-area-positive" :d="pnlAreaPath" clip-path="url(#profilePnlClipTop)" />
                      <path class="pnl-area-negative" :d="pnlAreaPath" clip-path="url(#profilePnlClipBottom)" />

                      <path class="pnl-line-positive" :d="pnlLinePath" clip-path="url(#profilePnlClipTop)" />
                      <path class="pnl-line-negative" :d="pnlLinePath" clip-path="url(#profilePnlClipBottom)" />

                      <circle :cx="pnlDot.x" :cy="pnlDot.y" r="8.2" class="pnl-dot-halo" />
                      <circle :cx="pnlDot.x" :cy="pnlDot.y" r="4.2" class="pnl-dot" />
                    </svg>
                    <div class="pnl-mini-chart__x-axis">
                      <span>{{ pnlRangeStartLabel }}</span>
                      <span>{{ pnlRangeEndLabel }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

        </main>

        <aside class="profile-sidebar">
          <section class="profile-card profile-achievements-card">
            <div class="section-heading">
              <div>
                <p class="section-kicker">Marcos</p>
                <h2 class="achievements-title">Conquistas</h2>
              </div>
              <div class="section-pill">
                {{ achievementUnlockCount }} / {{ achievementsView.length }} desbloqueadas
              </div>
            </div>

            <div class="achievement-grid profile-achievement-grid">
              <article
                v-for="(achv, index) in achievementsView"
                :key="index"
                class="achievement-card profile-achievement-card"
                :class="{ 'is-unlocked': achv.achieved, 'is-locked': !achv.achieved }"
              >
                <div class="badge-icon profile-achievement-icon">
                  <i :class="achv.icon"></i>
                </div>
                <div class="achievement-info profile-achievement-info">
                  <p class="achievement-title profile-achievement-title">{{ achv.title }}</p>
                  <p class="achievement-meta profile-achievement-meta">{{ achv.meta }}</p>
                </div>
                <div class="achievement-status profile-achievement-status">
                  <span class="achievement-progress profile-achievement-progress">{{ achv.progressText }}</span>
                  <span class="achievement-state profile-achievement-state" :class="achv.achieved ? 'is-done' : 'is-pending'">
                    <i :class="achv.achieved ? 'fas fa-check' : 'fas fa-lock'"></i>
                  </span>
                </div>
              </article>
            </div>
          </section>
        </aside>
      </div>
    </div>

    <div v-if="false" class="profile-layout">
      <section class="profile-user-card profile-user-card--restructured">
        <div class="profile-hero-top">
          <div class="profile-hero-identity">
            <div class="avatar-stack" @click="triggerFileInput">
              <div class="avatar-ring">
                <div class="avatar-shell">
                  <template v-if="avatarUrl">
                    <img :src="avatarUrl" alt="User Avatar" class="avatar-img" />
                  </template>
                  <template v-else>
                    <div class="avatar-fallback">{{ avatarInitials }}</div>
                  </template>
                  <div class="avatar-overlay">
                    <i class="fas fa-camera"></i>
                  </div>
                </div>
              </div>
              <input
                type="file"
                ref="fileInput"
                accept="image/png,image/jpeg,image/webp,image/avif"
                @change="onFileChange"
                hidden
              />
              <button
                v-if="avatarDirty"
                class="avatar-save-btn"
                type="button"
                @click.stop="saveAvatarChange"
                aria-label="Salvar foto de perfil"
              >
                <i class="fas fa-check"></i>
              </button>
              <div class="avatar-level-chip">LVL {{ userLevel }}</div>
            </div>

            <div class="profile-hero-copy">
              <div class="name-row">
                <div class="name-block">
                  <template v-if="!editUsername">
                    <h1 class="display-name">{{ username }}</h1>
                  </template>
                  <template v-else>
                    <input
                      type="text"
                      v-model="usernameEdit"
                      class="display-input"
                      @input="handleUsernameInput"
                    />
                  </template>
                  <p class="user-handle">{{ userHandle }}</p>
                  <p class="connection-row" :class="discordConnected ? 'connection-row--connected' : 'connection-row--idle'">
                    <i class="fab fa-discord"></i>
                    <span>{{ discordStatusLabel }}</span>
                  </p>
                </div>
                <div class="name-actions">
                  <button
                    v-if="editUsername"
                    class="icon-button"
                    @click="toggleEditUsername"
                    :aria-label="editActionLabel"
                  >
                    <i class="fas" :class="editActionIcon"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <aside class="profile-rank-card">
            <div class="profile-rank-card__orb">
              <i class="fas" :class="showTierBadge ? activeTierBadge.icon : 'fa-lock'"></i>
            </div>
            <div class="profile-rank-card__body">
              <span class="profile-rank-card__kicker">Patente Atual</span>
              <span class="profile-rank-card__title">{{ showTierBadge ? activeTierBadge.label : 'Insignia de Tier' }}</span>
              <div class="profile-rank-card__meta">
                <span class="profile-rank-card__dots"><i></i><i></i><i></i></span>
                <span>Nível {{ userLevel }}</span>
              </div>
            </div>
          </aside>
        </div>

        <div class="profile-hero-divider"></div>

        <div class="profile-hero-bottom">
          <div class="profile-inline-progress profile-inline-progress--hero">
            <div class="profile-inline-progress-head">
              <div class="stat-number">{{ userXP }} / 100 XP</div>
              <div class="xp-meta">
                Faltam <span class="xp-remaining">{{ 100 - userXP }}</span> XP para o próximo nível.
              </div>
            </div>
            <div class="xp-bar">
              <div class="xp-fill" :style="{ width: userXP + '%' }"></div>
              <div class="xp-marker" :style="{ left: userXP + '%' }"></div>
              <div
                v-for="m in xpMilestones"
                :key="`profile-inline-xp-${m}`"
                v-show="userXP < m"
                class="xp-milestone"
                :style="{ left: m + '%' }"
              >
                <span class="xp-m-dot"></span>
              </div>
            </div>
          </div>

          <div class="cta-row cta-row--hero">
            <button
              class="btn-primary cta-action cta-action--wallet"
              type="button"
              @click="walletConnected ? disconnectBybit() : deposit()"
            >
              <i class="fas fa-wallet"></i>
              {{ walletConnected ? 'Desconectar carteira' : 'Conectar carteira' }}
            </button>
            <div class="cta-icon-row">
              <button
                class="icon-button cta-action--icon"
                type="button"
                @click="handleDiscordConnect"
                :disabled="isDiscordLoading"
                :aria-label="discordConnected ? 'Reconectar Discord' : 'Conectar Discord'"
              >
                <i class="fab" :class="isDiscordLoading ? 'fa-spinner fa-spin' : 'fa-discord'"></i>
              </button>
              <button
                v-if="discordConnected"
                class="icon-button cta-action--icon"
                type="button"
                @click="disconnectDiscord"
                :disabled="isDiscordDisconnecting"
                aria-label="Desconectar Discord"
              >
                <i class="fas" :class="isDiscordDisconnecting ? 'fa-spinner fa-spin' : 'fa-unlink'"></i>
              </button>
              <button
                v-if="!editUsername"
                class="icon-button cta-action--icon"
                type="button"
                @click="toggleEditUsername"
                aria-label="Mudar nome"
              >
                <i class="fas fa-pen"></i>
              </button>
              <button
                class="icon-button cta-action--icon"
                type="button"
                @click="openChangePasswordModal"
                aria-label="Alterar senha"
              >
                <i class="fas fa-key"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section v-if="false" class="profile-user-card">
        <div class="identity">
          <div class="avatar-stack" @click="triggerFileInput">
            <div class="avatar-ring">
              <div class="avatar-shell">
                <template v-if="avatarUrl">
                  <img :src="avatarUrl" alt="User Avatar" class="avatar-img" />
                </template>
                <template v-else>
                  <div class="avatar-fallback">{{ avatarInitials }}</div>
                </template>
                <div class="avatar-overlay">
                  <i class="fas fa-camera"></i>
                </div>
              </div>
            </div>
            <input
              type="file"
              ref="fileInput"
              accept="image/png,image/jpeg,image/webp,image/avif"
              @change="onFileChange"
              hidden
            />
            <button
              v-if="avatarDirty"
              class="avatar-save-btn"
              type="button"
              @click.stop="saveAvatarChange"
              aria-label="Salvar foto de perfil"
            >
              <i class="fas fa-check"></i>
            </button>
          </div>

          <div class="identity-main">
            <div class="name-row">
              <div class="name-block">
                <template v-if="!editUsername">
                  <h1 class="display-name">{{ username }}</h1>
                </template>
                <template v-else>
                  <input
                    type="text"
                    v-model="usernameEdit"
                    class="display-input"
                    @input="handleUsernameInput"
                  />
                </template>
                <p class="user-handle">{{ userHandle }}</p>
                <p class="connection-row" :class="discordConnected ? 'connection-row--connected' : 'connection-row--idle'">
                  <i class="fab fa-discord"></i>
                  <span>{{ discordStatusLabel }}</span>
                </p>
              </div>
              <div class="name-actions">
                <button
                  v-if="editUsername"
                  class="icon-button"
                  @click="toggleEditUsername"
                  :aria-label="editActionLabel"
                >
                  <i class="fas" :class="editActionIcon"></i>
                </button>
              </div>
            </div>
            <div class="profile-inline-progress">
              <div class="profile-inline-progress-head">
                <div class="profile-inline-progress-copy">
                  <div class="profile-inline-progress-kicker">Progressao</div>
                  <div class="stat-number">{{ userXP }} / 100 XP</div>
                  <div class="xp-meta">
                    Faltam
                    <span class="xp-remaining">{{ 100 - userXP }}</span>
                    XP para o próximo nível.
                  </div>
                </div>
                <div class="level-band level-band--compact" :class="levelBandTierClass">
                  <span class="level-band__text">
                    <i class="fas fa-sparkles"></i>
                    Nível {{ userLevel }}
                  </span>
                </div>
              </div>

              <div class="xp-bar">
                <div class="xp-fill" :style="{ width: userXP + '%' }"></div>
                <div class="xp-marker" :style="{ left: userXP + '%' }"></div>
                <div
                  v-for="m in xpMilestones"
                  :key="`profile-inline-xp-${m}`"
                  v-show="userXP < m"
                  class="xp-milestone"
                  :style="{ left: m + '%' }"
                >
                  <span class="xp-m-dot"></span>
                </div>
              </div>
            </div>

          </div>
        </div>

        <div class="identity-footer">
          <div class="cta-row">
            <button
              class="btn-primary cta-action"
              type="button"
              @click="walletConnected ? disconnectBybit() : deposit()"
            >
              <i class="fas fa-wallet"></i>
              {{ walletConnected ? 'Desconectar carteira' : 'Conectar carteira' }}
            </button>
            <button
              class="icon-button cta-action--icon"
              type="button"
              @click="handleDiscordConnect"
              :disabled="isDiscordLoading"
              :aria-label="discordConnected ? 'Reconectar Discord' : 'Conectar Discord'"
            >
              <i class="fab" :class="isDiscordLoading ? 'fa-spinner fa-spin' : 'fa-discord'"></i>
            </button>
            <button
              v-if="discordConnected"
              class="icon-button cta-action--icon"
              type="button"
              @click="disconnectDiscord"
              :disabled="isDiscordDisconnecting"
              aria-label="Desconectar Discord"
            >
              <i class="fas" :class="isDiscordDisconnecting ? 'fa-spinner fa-spin' : 'fa-unlink'"></i>
            </button>
            <button
              v-if="!editUsername"
              class="icon-button cta-action--icon"
              type="button"
              @click="toggleEditUsername"
              aria-label="Mudar nome"
            >
              <i class="fas fa-pen"></i>
            </button>
            <button
              class="icon-button cta-action--icon"
              type="button"
              @click="openChangePasswordModal"
              aria-label="Alterar senha"
            >
              <i class="fas fa-key"></i>
            </button>
          </div>
          <aside class="insignias-panel" aria-label="Insignias de perfil">
            <div class="insignias-list">
              <article v-if="discordConnected" class="insignia-badge insignia-badge--discord">
                <span class="insignia-badge__orb">
                  <i class="fab fa-discord"></i>
                </span>
                <span class="insignia-badge__body">
                  <span class="insignia-badge__name">Discord Conectado</span>
                  <span class="insignia-badge__meta">Conta vinculada ao ecossistema</span>
                </span>
              </article>
              <article v-if="showBetaTesterBadge" class="insignia-badge insignia-badge--beta">
                <span class="insignia-badge__orb">
                  <i class="fas fa-flask"></i>
                </span>
                <span class="insignia-badge__body">
                  <span class="insignia-badge__name">BetaTester</span>
                  <span class="insignia-badge__meta">Acesso antecipado</span>
                </span>
              </article>
              <article
                class="insignia-badge insignia-badge--tier"
                :class="[showTierBadge ? activeTierBadge.className : 'insignia-badge--locked']"
              >
                <span class="insignia-badge__orb">
                  <i class="fas" :class="showTierBadge ? activeTierBadge.icon : 'fa-lock'"></i>
                </span>
                <span class="insignia-badge__body">
                  <span class="insignia-badge__name">{{ showTierBadge ? activeTierBadge.label : 'Insígnia de Tier' }}</span>
                  <span class="insignia-badge__meta">{{ showTierBadge ? activeTierBadge.meta : 'Conecte a API' }}</span>
                </span>
              </article>
            </div>
          </aside>
        </div>
      </section>

      <aside v-if="false" class="profile-progress-panel">

        <div class="stat-card">
          <div class="progress-header">
          </div>

          <div class="xp-main">
            <div class="xp-headline">
              <div class="stat-number">
                {{ userXP }} / 100 XP
              </div>
              <div class="level-band level-band--compact" :class="levelBandTierClass">
                <span class="level-band__text">
                  <i class="fas fa-sparkles"></i>
                  Nível {{ userLevel }} - {{ levelTierTitle }}
                </span>
              </div>
            </div>
            <div class="xp-meta-row">
              <div class="xp-meta">
                Faltam
                <span class="xp-remaining">{{ 100 - userXP }} XP</span>
                para o próximo nível.
              </div>
              <span class="xp-dot">{{ userXP }}%</span>
            </div>
          </div>

          <div class="xp-bar">
            <div class="xp-fill" :style="{ width: userXP + '%' }"></div>
            <div class="xp-marker" :style="{ left: userXP + '%' }"></div>
            <div
              v-for="m in xpMilestones"
              :key="m"
              v-show="userXP < m"
              class="xp-milestone"
              :style="{ left: m + '%' }"
            >
              <span class="xp-m-dot"></span>
            </div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-number">{{ streakLabel }}</div>
          <div class="streak-strip">
            <div
              v-for="i in 7"
              :key="i"
              class="streak-slot"
              :class="{ active: i <= streakDays, hot: streakDays >= 5 }"
            ></div>
            <span class="streak-hint" v-if="streakDays >= 5">Hot streak</span>
            <span class="streak-hint" v-else>{{ streakDaysToHot }} dias para bônus</span>
          </div>
          <p class="stat-sub">Mantenha-se no ritmo para reforcar seu MMR interno.</p>
        </div>
      </aside>

      <section class="metrics-section profile-performance-section">
        <div class="section-heading">
          <div>
            <p class="section-kicker">Desempenho</p>
            <h2 class="profile-dashboard-title">Mini dashboard do player</h2>
          </div>
        </div>
        <div class="metrics-grid">
          <div class="metric-card" v-for="metric in metrics" :key="metric.label">
            <div class="metric-label">{{ metric.label }}</div>
            <div class="metric-value">{{ metric.value }}</div>
          </div>
        </div>
        <div class="pnl-spotlight-card">
          <div class="pnl-label">PNL 30D</div>
          <div class="pnl-value" :class="pnlValueClass">{{ pnl30d }}</div>
          <div class="pnl-chart" aria-hidden="true">
            <div class="pnl-mini-chart">
              <div class="pnl-mini-chart__y-axis">
                <svg class="axis-svg" :viewBox="`0 0 38 ${pnlChartHeight}`" role="presentation">
                  <text
                    v-for="(row, index) in pnlAxisRows"
                    :key="`profile-pnl-axis-${index}`"
                    class="axis-text"
                    x="36"
                    :y="row.y"
                    text-anchor="end"
                    :dominant-baseline="index === 0 ? 'hanging' : index === pnlAxisRows.length - 1 ? 'ideographic' : 'middle'"
                  >
                    {{ row.label }}
                  </text>
                </svg>
              </div>
              <div class="pnl-mini-chart__canvas">
                <svg :viewBox="`0 0 ${pnlChartWidth} ${pnlChartHeight}`" preserveAspectRatio="none" class="pnl-line-svg">
                  <defs>
                    <linearGradient id="profilePnlAreaPositive" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stop-color="#30c58d" stop-opacity="0.42" />
                      <stop offset="100%" stop-color="#30c58d" stop-opacity="0.05" />
                    </linearGradient>
                    <linearGradient id="profilePnlAreaNegative" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stop-color="#f05465" stop-opacity="0.4" />
                      <stop offset="100%" stop-color="#f05465" stop-opacity="0.08" />
                    </linearGradient>
                    <clipPath id="profilePnlClipTop">
                      <rect :x="0" :y="0" :width="pnlChartWidth" :height="pnlZeroY" />
                    </clipPath>
                    <clipPath id="profilePnlClipBottom">
                      <rect :x="0" :y="pnlZeroY" :width="pnlChartWidth" :height="pnlChartHeight - pnlZeroY" />
                    </clipPath>
                  </defs>

                  <line
                    v-for="(row, idx) in pnlGridRows"
                    :key="`profile-pnl-grid-${idx}`"
                    x1="0"
                    :y1="row.y"
                    :x2="pnlChartWidth"
                    :y2="row.y"
                    class="pnl-grid-line"
                  />

                  <line x1="0" :y1="pnlZeroY" :x2="pnlChartWidth" :y2="pnlZeroY" class="pnl-grid-zero" />

                  <path class="pnl-area-positive" :d="pnlAreaPath" clip-path="url(#profilePnlClipTop)" />
                  <path class="pnl-area-negative" :d="pnlAreaPath" clip-path="url(#profilePnlClipBottom)" />

                  <path class="pnl-line-positive" :d="pnlLinePath" clip-path="url(#profilePnlClipTop)" />
                  <path class="pnl-line-negative" :d="pnlLinePath" clip-path="url(#profilePnlClipBottom)" />

                  <circle :cx="pnlDot.x" :cy="pnlDot.y" r="8.2" class="pnl-dot-halo" />
                  <circle :cx="pnlDot.x" :cy="pnlDot.y" r="4.2" class="pnl-dot" />
                </svg>
                <div class="pnl-mini-chart__x-axis">
                  <span>{{ pnlRangeStartLabel }}</span>
                  <span>{{ pnlRangeEndLabel }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="metrics-section profile-recognition-section">
        <div class="section-heading">
          <div>
            <p class="section-kicker">Reconhecimento</p>
            <h2 class="profile-dashboard-title profile-dashboard-title--light">Insígnias</h2>
          </div>
        </div>
        <aside class="insignias-panel insignias-panel--standalone" aria-label="Insignias de perfil">
          <div class="insignias-list">
            <article v-if="discordConnected" class="insignia-badge insignia-badge--discord">
              <span class="insignia-badge__orb">
                <i class="fab fa-discord"></i>
              </span>
              <span class="insignia-badge__body">
                <span class="insignia-badge__name">Discord Conectado</span>
                <span class="insignia-badge__meta">Conta vinculada</span>
              </span>
            </article>
            <article v-if="showBetaTesterBadge" class="insignia-badge insignia-badge--beta">
              <span class="insignia-badge__orb">
                <i class="fas fa-flask"></i>
              </span>
              <span class="insignia-badge__body">
                <span class="insignia-badge__name">BetaTester</span>
                <span class="insignia-badge__meta">Acesso antecipado</span>
              </span>
            </article>
            <article
              class="insignia-badge insignia-badge--tier"
              :class="[showTierBadge ? activeTierBadge.className : 'insignia-badge--locked']"
            >
              <span class="insignia-badge__orb">
                <i class="fas" :class="showTierBadge ? activeTierBadge.icon : 'fa-lock'"></i>
              </span>
              <span class="insignia-badge__body">
                <span class="insignia-badge__name">{{ showTierBadge ? activeTierBadge.label : 'Insignia de Tier' }}</span>
                <span class="insignia-badge__meta">{{ showTierBadge ? activeTierBadge.meta : 'Conecte a API' }}</span>
              </span>
            </article>
          </div>
        </aside>
      </section>

      <section class="achievements-section profile-achievements-section">
        <div class="section-heading">
          <div>
            <p class="section-kicker">Marcos</p>
            <h2 class="achievements-title">Conquistas</h2>
          </div>
          <div class="section-pill">{{ achievementsView.filter((achv) => achv.achieved).length }} / {{ achievementsView.length }} desbloqueadas</div>
        </div>

        <div class="achievement-grid">
          <div
            v-for="(achv, index) in achievementsView"
            :key="index"
            class="achievement-card"
            :class="{ unlocked: achv.achieved, locked: !achv.achieved }"
          >
            <div class="badge-icon">
              <i :class="achv.icon"></i>
            </div>
            <div class="achievement-info">
              <p class="achievement-title">{{ achv.title }}</p>
              <p class="achievement-meta">{{ achv.meta }}</p>
            </div>
            <div class="achievement-status">
              <span class="achievement-progress">{{ achv.progressText }}</span>
              <span class="achievement-state" :class="achv.achieved ? 'done' : 'pending'">
                <i :class="achv.achieved ? 'fas fa-check' : 'fas fa-lock'"></i>
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <button type="button" class="modal-close" @click="closeModal" aria-label="Fechar modal">
          <i class="fas fa-times"></i>
        </button>
        <h2>Conectar carteira</h2>
        <p class="subtext">Insira sua Bybit API Key e API Secret</p>
        <form class="modal-form" @submit.prevent="saveApiKeys">
          <div class="textfield">
            <i class="fas fa-key"></i>
            <input type="text" v-model="apiKey" placeholder="API Key" required />
          </div>
          <div class="textfield">
            <i class="fas fa-lock"></i>
            <input type="text" v-model="apiSecret" placeholder="API Secret" required />
          </div>
          <div class="modal-actions">
            <button type="submit" class="btn-submit" :disabled="isConnectingBybit">
              {{ isConnectingBybit ? 'Conectando...' : 'Salvar' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="showPasswordModal" class="modal-overlay" @click.self="closeChangePasswordModal">
      <div class="modal-content">
        <button type="button" class="modal-close" @click="closeChangePasswordModal" aria-label="Fechar modal">
          <i class="fas fa-times"></i>
        </button>
        <h2>Alterar senha</h2>
        <p class="subtext">Informe sua senha atual e defina uma nova senha.</p>
        <form class="modal-form" @submit.prevent="submitPasswordChange">
          <div class="textfield">
            <i class="fas fa-lock"></i>
            <input
              type="password"
              v-model="currentPassword"
              placeholder="Senha atual"
              autocomplete="current-password"
              required
            />
          </div>
          <div class="textfield">
            <i class="fas fa-key"></i>
            <input
              type="password"
              v-model="newPassword"
              placeholder="Nova senha"
              autocomplete="new-password"
              required
            />
          </div>
          <div class="textfield">
            <i class="fas fa-key"></i>
            <input
              type="password"
              v-model="confirmNewPassword"
              placeholder="Confirmar nova senha"
              autocomplete="new-password"
              required
            />
          </div>
          <div class="modal-actions">
            <button type="submit" class="btn-submit" :disabled="isChangingPassword">
              {{ isChangingPassword ? 'Salvando...' : 'Salvar nova senha' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="showDisconnectModal" class="modal-overlay" @click.self="closeDisconnectModal">
      <div class="modal-content">
        <button type="button" class="modal-close" @click="closeDisconnectModal" aria-label="Fechar modal">
          <i class="fas fa-times"></i>
        </button>
        <h2>Desconectar carteira</h2>
        <p class="subtext">Tem certeza que deseja desconectar sua carteira Bybit?</p>
        <div class="modal-actions">
          <button type="button" class="btn-cancel" :disabled="isDisconnecting" @click="closeDisconnectModal">
            Cancelar
          </button>
          <button type="button" class="btn-submit btn-danger" :disabled="isDisconnecting" @click="confirmDisconnectBybit">
            {{ isDisconnecting ? 'Desconectando...' : 'Desconectar carteira' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_BASE_URL } from '@/config/env';
import { toastError, toastSuccess, toastWarning } from '@/utils/toast';

const PROFILE_AVATAR_KEY_PREFIX = 'profile_avatar_url';
const DEFAULT_AVATAR_URL = '/IMAGE-BASE.png';
const ALLOWED_AVATAR_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/avif']);
const MAX_AVATAR_BYTES = 2 * 1024 * 1024;
const PNL_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const USERNAME_MAX_LENGTH = 16;
const USERNAME_CHANGE_HISTORY_KEY = 'profile_username_change_history';
const USERNAME_CHANGE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const USERNAME_MAX_CHANGES_PER_WINDOW = 2;
const BADGE_SYNC_RETRY_DELAY_MS = 1200;
const BADGE_SYNC_RETRY_ATTEMPTS = 6;
const loadUsernameChangeHistory = () => {
  if (typeof localStorage === 'undefined') return [];
  const raw = localStorage.getItem(USERNAME_CHANGE_HISTORY_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
  } catch {
    return [];
  }
};

const persistUsernameChangeHistory = (history) => {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(USERNAME_CHANGE_HISTORY_KEY, JSON.stringify(history));
  } catch {
    // best effort
  }
};

const pruneUsernameChangeHistory = (history, now = Date.now()) => {
  if (!Array.isArray(history)) return [];
  return history.filter((timestamp) => now - timestamp <= USERNAME_CHANGE_WINDOW_MS);
};

const buildPnlGeometry = (values) => {
  const width = 420;
  const height = 128;
  const leftPad = 4;
  const rightPad = 16;
  const topPad = 10;
  const bottomPad = 16;
  const safeValues = Array.isArray(values) && values.length ? values : [0, 0];
  const min = Math.min(...safeValues);
  const max = Math.max(...safeValues);
  const span = max - min || 1;
  const drawableWidth = width - leftPad - rightPad;
  const stepX = safeValues.length > 1 ? drawableWidth / (safeValues.length - 1) : drawableWidth;

  const points = safeValues.map((value, index) => {
    const x = Number((leftPad + index * stepX).toFixed(2));
    const normalized = (value - min) / span;
    const y = Number((height - bottomPad - normalized * (height - topPad - bottomPad)).toFixed(2));
    return { x, y };
  });

  const linePath = points.map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x},${point.y}`).join(' ');
  const lastPoint = points[points.length - 1];
  const areaPath = `${linePath} L${width - rightPad},${height} L${leftPad},${height} Z`;
  const rawZeroY = height - bottomPad - ((0 - min) / span) * (height - topPad - bottomPad);
  const zeroY = Math.max(topPad, Math.min(height - bottomPad, rawZeroY));

  return {
    width,
    height,
    min,
    max,
    zeroY,
    linePath,
    areaPath,
    lastPoint
  };
};

export default {
  name: 'Profile',
  data() {
    return {
      avatarUrl: '',
      avatarDirty: false,
      username: '',
      editUsername: false,
      usernameEdit: '',
      usernameErrorMessage: '',
      usernameChangeHistory: [],

      userXP: 0,
      userLevel: 0,
      nextUnlock: 'Badge de risco ajustado',
      streakDays: 0,

      metrics: [
        { key: 'executed', label: 'Trades executadas', value: '-' },
        { key: 'winrate', label: 'Winrate', value: '-' },
        { key: 'streak', label: 'Maior sequência', value: '-' },
        { key: 'best', label: 'Maior lucro', value: '-' }
      ],
      pnl30dRoi: 0,
      pnlDailySeries: [],
      pnlRefreshTimer: null,

      achievements: [
        { key: 'account_connected', title: 'N\u00FAcleo Conectado', icon: 'fas fa-link', meta: 'fluxo operacional ativo' },
        { key: 'first_trade', title: 'Primeira Execu\u00E7\u00E3o', icon: 'fas fa-medal', meta: 'voc\u00EA entrou no fluxo' },
        { key: 'profit_100', title: 'N\u00FAcleo em Expans\u00E3o', icon: 'fas fa-trophy', meta: '+100 USDT realizado' },
        { key: 'trades_25', title: 'Ritmo Operacional', icon: 'fas fa-award', meta: '25 opera\u00E7\u00F5es conclu\u00EDdas' },
        { key: 'safe_mode', title: 'Disciplina Blindada', icon: 'fas fa-shield-alt', meta: '10 opera\u00E7\u00F5es vencedoras' }
      ],
      unlockedAchievementKeys: [],
      tradeStats: {
        executed: 0,
        wins: 0,
        losses: 0,
        maxWinStreak: 0,
        maxLossStreak: 0,
        bestProfit: 0
      },
      userBadges: [],
      badgesLoaded: false,

      showModal: false,
      apiKey: '',
      apiSecret: '',
      isConnectingBybit: false,

      showPasswordModal: false,
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
      isChangingPassword: false,

      discordConnection: null,
      isDiscordLoading: false,
      isDiscordDisconnecting: false,

      showDisconnectModal: false,
      isDisconnecting: false
    };
  },
  computed: {
    storeUsername() {
      return this.$store.getters.username;
    },
    walletConnected() {
      return this.$store.getters.walletConnected;
    },
    discordConnected() {
      return !!this.discordConnection?.connected;
    },
    discordDisplayName() {
      return this.discordConnection?.global_name || this.discordConnection?.username || '';
    },
    discordStatusLabel() {
      if (!this.discordConnected) return 'Discord nao conectado';
      const label = this.discordDisplayName || 'Conta conectada';
      return `Discord conectado: ${label}`;
    },
    streakLabel() {
      return `${this.streakDays} vitórias seguidas`;
    },
    streakDaysToHot() {
      return Math.max(0, 7 - this.streakDays);
    },
    avatarInitials() {
      return (this.username || 'Zima')
        .split(/[^a-zA-Z0-9]+/)
        .filter(Boolean)
        .map(part => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase() || 'Z';
    },
    userHandle() {
      const discordUsername = String(this.discordConnection?.username || '').trim();
      if (discordUsername) {
        return `@${discordUsername}`;
      }
      const discordGlobalName = String(this.discordConnection?.global_name || '').trim();
      if (discordGlobalName) {
        const normalizedDiscordName = discordGlobalName.replace(/\s+/g, '');
        return `@${normalizedDiscordName || 'discord'}`;
      }
      const base = (this.username || 'usuario')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '');
      return `@${base || 'usuario'}`;
    },
    editActionIcon() {
      if (!this.editUsername) return 'fa-pen';
      return this.usernameErrorMessage ? 'fa-times' : 'fa-check';
    },
    editActionLabel() {
      if (!this.editUsername) return 'Editar nome';
      return this.usernameErrorMessage ? 'Fechar edição' : 'Salvar nome';
    },
    levelTierTitle() {
      if (this.userLevel === 0) return 'Iniciante';
      if (this.userLevel <= 10) return 'Trader Bronze';
      if (this.userLevel <= 25) return 'Trader Prata';
      if (this.userLevel <= 50) return 'Trader Ouro';
      return 'Trader Rubi';
    },
    levelBandTierClass() {
      if (this.userLevel === 0) return 'level-band--rookie';
      if (this.userLevel <= 10) return 'level-band--bronze';
      if (this.userLevel <= 25) return 'level-band--silver';
      if (this.userLevel <= 50) return 'level-band--gold';
      return 'level-band--ruby';
    },
    traderTierBadge() {
      if (this.userLevel === 0) {
        return { label: 'Iniciante', meta: 'Nível 0', className: 'insignia-badge--rookie', icon: 'fa-seedling' };
      }
      if (this.userLevel <= 10) {
        return { label: 'Trader Bronze', meta: `Nível ${this.userLevel}`, className: 'insignia-badge--bronze', icon: 'fa-shield-alt' };
      }
      if (this.userLevel <= 25) {
        return { label: 'Trader Prata', meta: `Nível ${this.userLevel}`, className: 'insignia-badge--silver', icon: 'fa-medal' };
      }
      if (this.userLevel <= 50) {
        return { label: 'Trader Ouro', meta: `Nível ${this.userLevel}`, className: 'insignia-badge--gold', icon: 'fa-crown' };
      }
      return { label: 'Trader Rubi', meta: `Nível ${this.userLevel}`, className: 'insignia-badge--ruby', icon: 'fa-gem' };
    },
    showBetaTesterBadge() {
      if (!this.badgesLoaded) return true;
      return this.userBadges.some((badge) => badge.key === 'betatester');
    },
    showTierBadge() {
      if (!this.badgesLoaded) return false;
      return this.userBadges.some((badge) => badge.key.startsWith('level_'));
    },
    activeTierBadge() {
      const fallback = this.traderTierBadge;
      const tierByKey = {
        level_rookie: { className: 'insignia-badge--rookie', icon: 'fa-seedling' },
        level_bronze: { className: 'insignia-badge--bronze', icon: 'fa-shield-alt' },
        level_silver: { className: 'insignia-badge--silver', icon: 'fa-medal' },
        level_gold: { className: 'insignia-badge--gold', icon: 'fa-crown' },
        level_ruby: { className: 'insignia-badge--ruby', icon: 'fa-gem' }
      };

      let expectedTierKey = 'level_rookie';
      if (this.userLevel > 0 && this.userLevel <= 10) expectedTierKey = 'level_bronze';
      if (this.userLevel > 10 && this.userLevel <= 25) expectedTierKey = 'level_silver';
      if (this.userLevel > 25 && this.userLevel <= 50) expectedTierKey = 'level_gold';
      if (this.userLevel > 50) expectedTierKey = 'level_ruby';

      const tier = this.userBadges.find((badge) => badge.key === expectedTierKey);
      if (!tier) return fallback;

      const tierVisual = tierByKey[expectedTierKey] || { className: fallback.className, icon: fallback.icon };

      return {
        label: tier.label || fallback.label,
        meta: fallback.meta,
        className: tierVisual.className,
        icon: tierVisual.icon
      };
    },
    pnl30d() {
      const sign = this.pnl30dRoi >= 0 ? '+' : '';
      return `${sign}${this.pnl30dRoi.toFixed(2)}%`;
    },
    pnlSeriesWithLivePoint() {
      const baseSeries =
        Array.isArray(this.pnlDailySeries) && this.pnlDailySeries.length
          ? this.pnlDailySeries.slice()
          : [0, 0];
      if (baseSeries.length === 1) baseSeries.push(baseSeries[0]);
      const liveRoi = Number(this.pnl30dRoi);
      if (Number.isFinite(liveRoi)) {
        baseSeries[baseSeries.length - 1] = liveRoi;
      }
      return baseSeries;
    },
    pnlGeometry() {
      return buildPnlGeometry(this.pnlSeriesWithLivePoint);
    },
    pnlLinePath() {
      return this.pnlGeometry.linePath;
    },
    pnlAreaPath() {
      return this.pnlGeometry.areaPath;
    },
    pnlDot() {
      return this.pnlGeometry.lastPoint;
    },
    pnlChartWidth() {
      return this.pnlGeometry.width || 420;
    },
    pnlChartHeight() {
      return this.pnlGeometry.height || 128;
    },
    pnlZeroY() {
      return this.pnlGeometry.zeroY || (this.pnlChartHeight / 2);
    },
    pnlAxisRows() {
      const min = Number(this.pnlGeometry.min || 0);
      const max = Number(this.pnlGeometry.max || 0);
      const mid = (min + max) / 2;
      const rows = [
        { value: max, y: 10 },
        { value: mid, y: this.pnlChartHeight / 2 },
        { value: min, y: this.pnlChartHeight - 16 }
      ];
      return rows.map((row) => ({
        y: row.y,
        label: this.formatPnlAxisLabel(row.value)
      }));
    },
    pnlGridRows() {
      return this.pnlAxisRows.map((row) => ({ y: row.y }));
    },
    pnlRangeStartLabel() {
      return 'Início';
    },
    pnlRangeEndLabel() {
      return 'Agora';
    },
    pnlValueClass() {
      return this.pnl30dRoi >= 0 ? 'value-pos' : 'value-neg';
    },
    xpMilestones() {
      return [25, 50, 75, 99];
    },
    achievementUnlockCount() {
      return this.achievementsView.filter((achv) => achv.achieved).length;
    },
    rankProgression() {
      const tiers = [
        { key: 'rookie', label: 'Iniciante', meta: 'Nível 0 - 10', min: 0, max: 10, icon: 'fa-seedling' },
        { key: 'bronze', label: 'Trader Bronze', meta: 'Nível 11 - 25', min: 11, max: 25, icon: 'fa-shield-alt' },
        { key: 'silver', label: 'Trader Prata', meta: 'Nível 26 - 50', min: 26, max: 50, icon: 'fa-medal' },
        { key: 'gold', label: 'Trader Ouro', meta: 'Nível 51+', min: 51, max: Infinity, icon: 'fa-crown' }
      ];

      return tiers.map((tier) => {
        const isCurrent = this.userLevel >= tier.min && this.userLevel <= tier.max;
        const isUnlocked = this.userLevel >= tier.min;
        let stateLabel = 'Bloqueada';
        if (isCurrent) stateLabel = 'Atual';
        else if (isUnlocked) stateLabel = 'Liberada';
        else stateLabel = `Falta ${Math.max(0, tier.min - this.userLevel)} lvl`;

        return {
          ...tier,
          isCurrent,
          isUnlocked,
          stateLabel
        };
      });
    },
    achievementsView() {
      const unlockedSet = new Set(this.unlockedAchievementKeys);
      return this.achievements.map((achv) => {
        const currentMap = {
          account_connected: this.walletConnected ? 1 : 0,
          first_trade: this.tradeStats.executed,
          profit_100: this.tradeStats.bestProfit >= 100 ? 1 : 0,
          trades_25: this.tradeStats.executed,
          safe_mode: this.tradeStats.wins
        };
        const targetMap = {
          account_connected: 1,
          first_trade: 1,
          profit_100: 1,
          trades_25: 25,
          safe_mode: 10
        };
        const current = Number(currentMap[achv.key] ?? 0);
        const target = Number(targetMap[achv.key] ?? 1);
        const cappedCurrent = Math.min(current, target);
        const achieved = unlockedSet.has(achv.key) || current >= target;
        return {
          ...achv,
          achieved,
          progressText: achieved ? `${target}/${target}` : `${cappedCurrent}/${target}`
        };
      });
    }
  },
  watch: {
    storeUsername(next) {
      this.username = next;
      if (!this.editUsername) {
        this.usernameEdit = next;
      }
    },
    walletConnected(next, previous) {
      if (!next || previous) return;
      this.syncTierBadgeAfterWalletConnect();
    },
    '$route.query.openWallet': {
      immediate: true,
      handler(value) {
        if (!this.shouldAutoOpenFlag(value)) return;
        this.deposit();
        this.clearQueryFlag('openWallet');
      }
    },
    '$route.query.discord': {
      immediate: true,
      handler() {
        this.handleDiscordOAuthFeedback();
      }
    }
  },
  mounted() {
    this.$store.dispatch('hydrateFromStorage');
    this.username = this.storeUsername;
    this.usernameEdit = this.storeUsername;
    this.usernameChangeHistory = loadUsernameChangeHistory();
    this.loadAvatarFromStorage();
    this.fetchCurrentUserProfile();
    this.fetchUserBadges();
    this.fetchUserAchievements();
    this.fetchBybitConnection();
    this.refreshProfileData();
    this.pnlRefreshTimer = window.setInterval(() => this.refreshProfileData(), PNL_REFRESH_INTERVAL_MS);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  beforeUnmount() {
    if (this.pnlRefreshTimer) {
      window.clearInterval(this.pnlRefreshTimer);
      this.pnlRefreshTimer = null;
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  },
  methods: {
    handleVisibilityChange() {
      if (!document.hidden) {
        this.refreshProfileData();
      }
    },
    getAuthToken() {
      return localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    },
    async fetchCurrentUserProfile() {
      const token = this.getAuthToken();
      if (!token) return;

      try {
        const response = await axios.get(`${API_BASE_URL}/api/v1/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const user = response?.data;
        if (!user || typeof user !== 'object') return;
        this.$store.dispatch('setUser', user);
        this.username = user.user_name || this.username;
        this.usernameEdit = this.username;
        this.avatarUrl = user.avatar_url || DEFAULT_AVATAR_URL;
        this.discordConnection = user.discord || null;
        this.avatarDirty = false;
        window.dispatchEvent(new CustomEvent('profile-avatar-updated', { detail: this.avatarUrl }));
      } catch (error) {
        console.error('Erro ao sincronizar perfil do usuário:', error);
      }
    },
    shouldAutoOpenFlag(value) {
      const normalized = String(value || '').trim().toLowerCase();
      return normalized === '1' || normalized === 'true' || normalized === 'yes';
    },
    clearQueryFlag(flagKey) {
      const nextQuery = { ...this.$route.query };
      if (!(flagKey in nextQuery)) return;
      delete nextQuery[flagKey];
      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query: nextQuery,
        hash: this.$route.hash
      });
    },
    clearQueryFlags(flagKeys = []) {
      const nextQuery = { ...this.$route.query };
      let changed = false;
      flagKeys.forEach((flagKey) => {
        if (flagKey in nextQuery) {
          delete nextQuery[flagKey];
          changed = true;
        }
      });
      if (!changed) return;
      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query: nextQuery,
        hash: this.$route.hash
      });
    },
    handleDiscordOAuthFeedback() {
      const status = String(this.$route.query.discord || '').trim().toLowerCase();
      if (!status) return;

      const message = String(this.$route.query.discord_message || '').trim();
      if (status === 'connected') {
        toastSuccess(message || 'Conta Discord conectada com sucesso.');
        this.fetchCurrentUserProfile();
      } else if (status === 'error') {
        toastError(message || 'Nao foi possivel conectar sua conta Discord.');
      }

      this.clearQueryFlags(['discord', 'discord_message']);
    },
    getAvatarStorageKey() {
      const user = this.$store?.state?.user || {};
      const token =
        (typeof localStorage !== 'undefined' && localStorage.getItem('token')) ||
        (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('token')) ||
        '';
      const storedUsername = typeof localStorage !== 'undefined' ? localStorage.getItem('username') : '';
      const identity =
        user.id ||
        user.user_id ||
        user.email ||
        user.wallet_address ||
        user.account_id ||
        user.username ||
        user.user_name ||
        user.name ||
        storedUsername ||
        token ||
        'guest';
      const normalized = String(identity).trim().toLowerCase().replace(/[^a-z0-9@._-]/g, '_');
      return `${PROFILE_AVATAR_KEY_PREFIX}:${normalized || 'guest'}`;
    },
    loadAvatarFromStorage() {
      const user = this.$store?.state?.user || {};
      const avatarFromUser = user.avatar_url || user.avatarUrl || '';
      if (avatarFromUser) {
        this.avatarUrl = avatarFromUser;
        this.avatarDirty = false;
        return;
      }
      if (typeof localStorage === 'undefined') {
        this.avatarUrl = DEFAULT_AVATAR_URL;
        this.avatarDirty = false;
        return;
      }
      const key = this.getAvatarStorageKey();
      const storedAvatar = localStorage.getItem(key);
      this.avatarUrl = storedAvatar || DEFAULT_AVATAR_URL;
      this.avatarDirty = false;
    },
    async fetchUserBadges() {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        this.badgesLoaded = true;
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/api/v1/badges`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const rows = Array.isArray(response?.data?.badges) ? response.data.badges : [];
        this.userBadges = rows
          .map((badge) => ({
            key: String(badge?.key || '').trim().toLowerCase(),
            label: String(badge?.label || '').trim(),
            variant: String(badge?.variant || '').trim().toLowerCase()
          }))
          .filter((badge) => badge.key);
      } catch (error) {
        console.error('Erro ao buscar badges do perfil:', error);
      } finally {
        this.badgesLoaded = true;
      }
    },
    async syncTierBadgeAfterWalletConnect() {
      const hasTierBadge = () => this.userBadges.some((badge) => badge.key.startsWith('level_'));
      if (hasTierBadge()) return;

      for (let attempt = 0; attempt < BADGE_SYNC_RETRY_ATTEMPTS; attempt += 1) {
        await this.fetchUserBadges();
        if (hasTierBadge()) return;
        if (attempt < BADGE_SYNC_RETRY_ATTEMPTS - 1) {
          await new Promise((resolve) => window.setTimeout(resolve, BADGE_SYNC_RETRY_DELAY_MS));
        }
      }
    },
    async fetchUserAchievements() {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        this.unlockedAchievementKeys = [];
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/api/v1/achievements`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const rows = Array.isArray(response?.data?.achievements) ? response.data.achievements : [];
        this.unlockedAchievementKeys = rows
          .map((item) => String(item?.achievement_key || '').trim().toLowerCase())
          .filter(Boolean);
      } catch (error) {
        console.error('Erro ao buscar conquistas do perfil:', error);
      }
    },
    async persistUsername(name) {
      const currentName = this.$store.getters.username;
      const originalInput = typeof name === 'string' ? name : '';
      const rawInput = originalInput.trim();
      const nextName = rawInput || currentName;
      this.usernameErrorMessage = '';

      if (rawInput && /\s/.test(originalInput)) {
        this.usernameErrorMessage = 'O nome de usuario nao pode conter espacos.';
        toastWarning(this.usernameErrorMessage);
        return false;
      }

      if (rawInput && !this.validateUsernameLength(rawInput)) {
        return false;
      }

      if (rawInput && rawInput !== currentName && !this.ensureUsernameChangeQuota()) {
        return false;
      }

      const safeName = nextName || currentName;
      this.username = safeName;
      this.usernameEdit = safeName;

      try {
        const token = this.getAuthToken();
        if (!token) {
          toastError('Sessão expirada. Faça login novamente.');
          return false;
        }
        const response = await axios.patch(`${API_BASE_URL}/api/v1/users/me`, {
          user_name: safeName
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const nextUser = response?.data || { ...(this.$store?.state?.user || {}), user_name: safeName };
        this.$store.dispatch('setUser', nextUser);
        window.dispatchEvent(new CustomEvent('profile-username-updated', { detail: safeName }));
        if (rawInput && rawInput !== currentName) {
          this.recordUsernameChangeEvent();
        }
        return true;
      } catch (err) {
        console.error('Failed to persist username', err);
        return false;
      }
    },
    validateUsernameLength(value) {
      if (typeof value !== 'string') return true;
      if (value.length <= USERNAME_MAX_LENGTH) return true;
      this.usernameErrorMessage = `O nome deve ter no máximo ${USERNAME_MAX_LENGTH} caracteres.`;
      toastWarning(this.usernameErrorMessage);
      return false;
    },
    handleUsernameInput() {
      const value = typeof this.usernameEdit === 'string' ? this.usernameEdit : '';
      const sanitized = value.replace(/\s+/g, '');
      if (sanitized !== value) {
        this.usernameEdit = sanitized;
        this.usernameErrorMessage = 'O nome de usuario nao pode conter espacos.';
        return;
      }
      this.usernameErrorMessage = '';
    },
    ensureUsernameChangeQuota() {
      const now = Date.now();
      const pruned = pruneUsernameChangeHistory(this.usernameChangeHistory, now);
      this.usernameChangeHistory = pruned;
      persistUsernameChangeHistory(pruned);
      if (pruned.length >= USERNAME_MAX_CHANGES_PER_WINDOW) {
        this.usernameErrorMessage = 'Você pode alterar o nome no máximo duas vezes por semana.';
        toastWarning(this.usernameErrorMessage);
        return false;
      }
      return true;
    },
    recordUsernameChangeEvent() {
      const now = Date.now();
      const updated = pruneUsernameChangeHistory([...this.usernameChangeHistory, now], now);
      this.usernameChangeHistory = updated;
      persistUsernameChangeHistory(updated);
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    onFileChange(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file) return;
      if (!ALLOWED_AVATAR_TYPES.has(file.type)) {
        toastWarning('Formato inválido. Use PNG, JPG, WEBP ou AVIF (GIF e vídeo não são permitidos).');
        return;
      }
      if (file.size > MAX_AVATAR_BYTES) {
        toastWarning('Arquivo muito grande. Tamanho máximo recomendado: 2 MB.');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.avatarUrl = e.target.result;
        this.avatarDirty = true;
      };
      reader.readAsDataURL(file);
    },
    async saveAvatarChange() {
      if (!this.avatarUrl) return;
      let saved = false;
      try {
        const token = this.getAuthToken();
        if (!token) {
          toastError('Sessão expirada. Faça login novamente.');
          return;
        }
        const response = await axios.patch(`${API_BASE_URL}/api/v1/users/me`, {
          avatar_url: this.avatarUrl
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const nextUser = response?.data || { ...(this.$store?.state?.user || {}), avatar_url: this.avatarUrl };
        this.$store.dispatch('setUser', nextUser);
        window.dispatchEvent(new CustomEvent('profile-avatar-updated', { detail: this.avatarUrl }));
        saved = true;
      } catch (err) {
        console.error('Falha ao salvar avatar', err);
        toastError('Não foi possível salvar o avatar. Tente uma imagem menor.');
      } finally {
        this.avatarDirty = !saved;
      }
    },
    async toggleEditUsername() {
      if (this.editUsername) {
        if (this.usernameErrorMessage) {
          this.usernameEdit = this.username;
          this.usernameErrorMessage = '';
          this.editUsername = false;
          return;
        }
        if (!await this.persistUsername(this.usernameEdit)) return;
        this.editUsername = false;
        return;
      }
      this.usernameErrorMessage = '';
      this.usernameEdit = this.username;
      this.editUsername = true;
    },
    async handleDiscordConnect() {
      const token = this.getAuthToken();
      if (!token) {
        toastError('Sessao expirada. Faca login novamente.');
        return;
      }

      this.isDiscordLoading = true;
      try {
        const response = await axios.get(`${API_BASE_URL}/api/v1/discord/connect-url`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const authorizeURL = response?.data?.authorize_url;
        if (!authorizeURL) {
          throw new Error('URL de autorizacao ausente');
        }
        window.location.assign(authorizeURL);
      } catch (error) {
        const message = error?.response?.data?.error || 'Nao foi possivel iniciar a conexao com o Discord.';
        toastError(message);
      } finally {
        this.isDiscordLoading = false;
      }
    },
    async disconnectDiscord() {
      const token = this.getAuthToken();
      if (!token) {
        toastError('Sessao expirada. Faca login novamente.');
        return;
      }

      const confirmed = window.confirm('Deseja desconectar sua conta Discord da Zima?');
      if (!confirmed) return;

      this.isDiscordDisconnecting = true;
      try {
        const response = await axios.delete(`${API_BASE_URL}/api/v1/discord/connection`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        this.discordConnection = response?.data || { connected: false };
        await this.fetchCurrentUserProfile();
        toastSuccess('Conta Discord desconectada.');
      } catch (error) {
        const message = error?.response?.data?.error || 'Nao foi possivel desconectar o Discord.';
        toastError(message);
      } finally {
        this.isDiscordDisconnecting = false;
      }
    },
    deposit() {
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    openChangePasswordModal() {
      this.showPasswordModal = true;
    },
    closeChangePasswordModal() {
      this.showPasswordModal = false;
      this.currentPassword = '';
      this.newPassword = '';
      this.confirmNewPassword = '';
      this.isChangingPassword = false;
    },
    async submitPasswordChange() {
      if (!this.currentPassword || !this.newPassword || !this.confirmNewPassword) {
        toastWarning('Preencha todos os campos de senha.');
        return;
      }

      if (this.newPassword.length < 8) {
        toastWarning('A nova senha deve ter no minimo 8 caracteres.');
        return;
      }

      if (this.newPassword !== this.confirmNewPassword) {
        toastWarning('A confirmacao da senha nao confere.');
        return;
      }

      if (this.currentPassword === this.newPassword) {
        toastWarning('A nova senha deve ser diferente da senha atual.');
        return;
      }

      const token = this.getAuthToken();
      if (!token) {
        toastError('Sessao expirada. Faca login novamente.');
        return;
      }

      this.isChangingPassword = true;
      try {
        await axios.patch(`${API_BASE_URL}/api/v1/users/me/password`, {
          current_password: this.currentPassword,
          new_password: this.newPassword
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toastSuccess('Senha alterada com sucesso.');
        this.closeChangePasswordModal();
      } catch (error) {
        const message = error?.response?.data?.error || 'Nao foi possivel alterar a senha.';
        toastError(message);
      } finally {
        this.isChangingPassword = false;
      }
    },
    saveApiKeys() {
      this.connectBybit();
    },
    async saveProfile() {
      const nextName = this.editUsername ? this.usernameEdit : this.username;
      if (!await this.persistUsername(nextName)) return;
      this.editUsername = false;
      console.log('Perfil salvo:', { avatar: this.avatarUrl, username: this.username });
    },
    refreshProfileData() {
      if (typeof document !== 'undefined' && document.hidden) {
        return;
      }
      this.fetchPnlOverview();
      this.fetchBybitTradesSnapshot();
    },
    updateMetricValue(key, value) {
      const row = this.metrics.find(metric => metric.key === key);
      if (row) row.value = value;
    },
    formatUsd(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2
      }).format(value);
    },
    formatPnlAxisLabel(value) {
      const numeric = Number(value || 0);
      const abs = Math.abs(numeric);
      if (abs >= 1000) {
        return `${numeric >= 0 ? '+' : '-'}${(abs / 1000).toFixed(1)}k`;
      }
      return `${numeric >= 0 ? '+' : '-'}${abs.toFixed(1)}`;
    },
    applyTradeMetrics(trades) {
      if (!Array.isArray(trades) || trades.length === 0) {
        this.tradeStats = {
          executed: 0,
          wins: 0,
          losses: 0,
          maxWinStreak: 0,
          maxLossStreak: 0,
          bestProfit: 0
        };
        this.streakDays = 0;
        this.userXP = 0;
        this.userLevel = 0;
        this.updateMetricValue('executed', '-');
        this.updateMetricValue('winrate', '-');
        this.updateMetricValue('streak', '-');
        this.updateMetricValue('best', '-');
        return;
      }

      const executed = trades.length;
      const closed = trades.filter(trade => trade.state === 'closed');
      const wins = closed.filter(trade => Number(trade.pnl) > 0).length;
      const losses = closed.filter(trade => Number(trade.pnl) < 0).length;
      const winrate = closed.length > 0 ? (wins / closed.length) * 100 : 0;

      const sortedClosed = closed
        .slice()
        .sort((a, b) => Number(a.updated_time_ms || 0) - Number(b.updated_time_ms || 0));

      let maxWinStreak = 0;
      let maxLossStreak = 0;
      let currentWinStreak = 0;
      let currentLossStreak = 0;

      sortedClosed.forEach((trade) => {
        const pnl = Number(trade.pnl || 0);
        if (pnl > 0) {
          currentWinStreak += 1;
          currentLossStreak = 0;
          if (currentWinStreak > maxWinStreak) maxWinStreak = currentWinStreak;
        } else if (pnl < 0) {
          currentLossStreak += 1;
          currentWinStreak = 0;
          if (currentLossStreak > maxLossStreak) maxLossStreak = currentLossStreak;
        } else {
          currentWinStreak = 0;
          currentLossStreak = 0;
        }
      });

      const bestProfit = closed.reduce((acc, trade) => {
        const pnl = Number(trade.pnl || 0);
        return pnl > acc ? pnl : acc;
      }, 0);

      this.tradeStats = {
        executed,
        wins,
        losses,
        maxWinStreak,
        maxLossStreak,
        bestProfit
      };
      this.streakDays = maxWinStreak;

      // XP logic:
      // +5 por trade aberta/executada
      // +10 por trade fechada em lucro
      // +3 por trade fechada em prejuizo
      const totalXP =
        (executed * 5) +
        (wins * 10) +
        (losses * 3);
      this.userLevel = Math.floor(totalXP / 100);
      this.userXP = totalXP % 100;

      this.updateMetricValue('executed', String(executed));
      this.updateMetricValue('winrate', `${winrate.toFixed(1)}%`);
      this.updateMetricValue('streak', `${maxWinStreak}W / ${maxLossStreak}L`);
      this.updateMetricValue('best', bestProfit > 0 ? this.formatUsd(bestProfit) : '$0.00');
    },
    applyBackendSummary(summary, progress) {
      const executed = Number(summary?.executed || 0);
      const wins = Number(summary?.wins || 0);
      const losses = Number(summary?.losses || 0);
      const maxWinStreak = Number(summary?.max_win_streak || 0);
      const maxLossStreak = Number(summary?.max_loss_streak || 0);
      const bestProfit = Number(summary?.best_profit || 0);
      const winRate = Number(summary?.win_rate || 0);

      this.tradeStats = {
        executed,
        wins,
        losses,
        maxWinStreak,
        maxLossStreak,
        bestProfit
      };
      this.streakDays = maxWinStreak;

      this.userLevel = Number(progress?.level || 0);
      this.userXP = Number(progress?.xp_current || 0);

      this.updateMetricValue('executed', String(executed));
      this.updateMetricValue('winrate', `${winRate.toFixed(1)}%`);
      this.updateMetricValue('streak', `${maxWinStreak}W / ${maxLossStreak}L`);
      this.updateMetricValue('best', bestProfit > 0 ? this.formatUsd(bestProfit) : '$0.00');
    },
    async connectBybit() {
      if (!this.apiKey || !this.apiSecret) {
        toastWarning('Informe API Key e API Secret.');
        return;
      }

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        toastWarning('Faça login antes de conectar à Bybit.');
        return;
      }

      this.isConnectingBybit = true;
      try {
        const response = await axios.post(
          `${API_BASE_URL}/api/v1/bybit/connect`,
          {
            api_key: this.apiKey,
            api_secret: this.apiSecret
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        console.log('Bybit conectada:', response.data);
        this.$store.dispatch('setBybitConnection', response.data);
        this.refreshProfileData();
        await this.syncTierBadgeAfterWalletConnect();
        this.fetchUserAchievements();
        toastSuccess('Bybit conectada com sucesso!');
        this.closeModal();
      } catch (error) {
        console.error('Erro ao conectar Bybit:', error);
        const message =
          error?.response?.data?.error ||
          'Erro ao conectar Bybit. Verifique suas credenciais.';
        toastError(message);
      } finally {
        this.isConnectingBybit = false;
      }
    },
    async disconnectBybit() {
      if (!this.walletConnected) {
        toastWarning('Nenhuma carteira conectada.');
        return;
      }
      this.showDisconnectModal = true;
    },
    closeDisconnectModal() {
      if (this.isDisconnecting) return;
      this.showDisconnectModal = false;
    },
    async confirmDisconnectBybit() {
      if (!this.walletConnected) {
        this.showDisconnectModal = false;
        toastWarning('Nenhuma carteira conectada.');
        return;
      }

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        this.showDisconnectModal = false;
        toastWarning('Faca login antes de desconectar a carteira.');
        return;
      }

      this.isDisconnecting = true;
      try {
        await axios.delete(`${API_BASE_URL}/api/v1/bybit/connection`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        this.$store.dispatch('clearBybitConnection');
        this.apiKey = '';
        this.apiSecret = '';
        this.showDisconnectModal = false;
        toastSuccess('Carteira desconectada com sucesso.');
        this.refreshProfileData();
      } catch (error) {
        const message = error?.response?.data?.error || 'Erro ao desconectar carteira.';
        toastError(message);
      } finally {
        this.isDisconnecting = false;
      }
    },
    async fetchBybitConnection() {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        this.$store.dispatch('clearBybitConnection');
        return;
      }
      try {
        const response = await axios.get(`${API_BASE_URL}/api/v1/bybit/connection`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        this.$store.dispatch('setBybitConnection', response.data);
      } catch (error) {
        if (error?.response?.status === 404) {
          this.$store.dispatch('clearBybitConnection');
          return;
        }
        console.error('Erro ao buscar conex?o Bybit:', error);
      }
    },
    async fetchPnlOverview() {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) return;

      try {
        const response = await axios.get(`${API_BASE_URL}/api/v1/bybit/overview`, {
          params: { range_days: 30 },
          headers: { Authorization: `Bearer ${token}` }
        });

        const data = response.data || {};
        this.pnl30dRoi = Number(data.roi) || 0;

        const daily = Array.isArray(data.daily) ? data.daily : [];
        const numericSeries = daily
          .map(row => Number(row?.pnl))
          .filter(value => Number.isFinite(value));

        this.pnlDailySeries = numericSeries.length >= 2 ? numericSeries : [0, this.pnl30dRoi];
      } catch (error) {
        console.error('Erro ao carregar PnL 30D no perfil:', error);
      }
    },
    async fetchBybitTradesSnapshot() {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        this.applyTradeMetrics([]);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/api/v1/bybit/trades`, {
          params: { limit: 300 },
          headers: { Authorization: `Bearer ${token}` }
        });
        const payload = response?.data || {};
        const trades = Array.isArray(payload.trades) ? payload.trades : [];
        if (payload.summary && payload.progress) {
          this.applyBackendSummary(payload.summary, payload.progress);
        } else {
          this.applyTradeMetrics(trades);
        }
      } catch (error) {
        console.error('Erro ao carregar métricas de trade no perfil:', error);
        this.applyTradeMetrics([]);
      }
    }
  }
};
</script>

<style scoped>
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css');
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&display=swap');

.profile-page {
  min-height: 100vh;
  padding: calc(var(--app-header-h) + 18px) var(--app-gutter) 40px;
  color: #e9ecf5;
  --profile-ring-gradient: linear-gradient(120deg, rgba(78, 115, 223, 0.9), rgba(155, 89, 182, 0.9));
  --profile-card-border: linear-gradient(
    120deg,
    rgba(78, 115, 223, 0.9),
    rgba(155, 89, 182, 0.9)
  );
  font-family: 'Montserrat', system-ui, -apple-system, sans-serif;
  background: #181a20;
  position: relative;
  overflow: hidden;
  isolation: isolate;
}

.profile-page::before {
  content: '';
  position: fixed;
  inset: 0;
  background: radial-gradient(circle at 16% 18%, rgba(78, 115, 223, 0.18), transparent 40%),
    radial-gradient(circle at 84% 8%, rgba(155, 89, 182, 0.16), transparent 42%),
    #181a20;
  z-index: 0;
  pointer-events: none;
  animation: bgDrift 18s ease-in-out infinite alternate;
}

.profile-page::after {
  content: '';
  position: fixed;
  inset: 0;
  background-image: linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 32px 32px;
  opacity: 0.35;
  z-index: 0;
  pointer-events: none;
}

.profile-page > * {
  position: relative;
  z-index: 1;
}

.profile-layout {
  display: grid;
  grid-template-columns: minmax(0, 2.2fr) minmax(320px, 1.2fr);
  grid-template-areas:
    'user user'
    'performance recognition'
    'achievements achievements';
  gap: 18px;
}

.profile-user-card {
  grid-area: user;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 28px 28px 18px;
  border-radius: 32px;
  background:
    linear-gradient(145deg, rgba(20, 24, 36, 0.95), rgba(16, 18, 28, 0.92)) padding-box,
    var(--profile-card-border) border-box;
  border: 1px solid transparent;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.05), inset 0 0 0 1px rgba(42, 47, 58, 0.7);
  overflow: hidden;
  transform: translateZ(0);
}

.profile-user-card::before {
  content: '';
  position: absolute;
  inset: -1px;
  z-index: -1;
  background: radial-gradient(circle at 18% 45%, rgba(78, 115, 223, 0.28), transparent 52%),
    radial-gradient(circle at 82% 35%, rgba(155, 89, 182, 0.26), transparent 58%);
  filter: blur(35px);
  opacity: 0.5;
  animation: heroPulse 9s ease-in-out infinite alternate;
  pointer-events: none;
}

.profile-user-card::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 24% 30%, rgba(78, 115, 223, 0.22), transparent 52%),
    radial-gradient(circle at 86% 60%, rgba(155, 89, 182, 0.14), transparent 60%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.03), transparent 55%);
  opacity: 0.45;
  filter: blur(38px);
  animation: heroFlow 16s ease-in-out infinite alternate;
  pointer-events: none;
}

.profile-user-card--restructured {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas:
    'top'
    'divider'
    'bottom';
  min-height: 0;
  padding: 26px 28px 28px;
  border-radius: 24px;
  background:
    linear-gradient(160deg, rgba(25, 28, 37, 0.98), rgba(20, 22, 30, 0.96)) padding-box,
    linear-gradient(90deg, rgba(78, 115, 223, 0.5), rgba(155, 89, 182, 0.44)) border-box;
  box-shadow:
    0 24px 60px rgba(0, 0, 0, 0.42),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.profile-user-card--restructured .identity,
.profile-user-card--restructured .identity-footer {
  display: none;
}

.profile-user-card--restructured::before {
  inset: auto auto -18% -10%;
  width: 44%;
  height: 72%;
  background: radial-gradient(circle, rgba(78, 115, 223, 0.16), transparent 68%);
  filter: blur(40px);
  opacity: 0.8;
}

.profile-user-card--restructured::after {
  inset: auto -12% -20% auto;
  width: 46%;
  height: 80%;
  background: radial-gradient(circle, rgba(155, 89, 182, 0.14), transparent 70%);
  filter: blur(44px);
  opacity: 0.75;
}

.profile-hero-top {
  grid-area: top;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(250px, 312px);
  gap: 28px;
  align-items: center;
  width: 100%;
}

.profile-hero-identity {
  display: grid;
  grid-template-columns: 132px minmax(0, 1fr);
  gap: 20px;
  align-items: center;
}

.profile-hero-copy {
  min-width: 0;
  padding-top: 0;
}

.profile-user-card--restructured .avatar-stack {
  min-height: 0;
  justify-content: flex-start;
}

.profile-user-card--restructured .avatar-ring {
  width: 112px;
  height: 112px;
  border-width: 4px;
  box-shadow:
    0 0 0 1px rgba(220, 232, 255, 0.16),
    0 18px 38px rgba(0, 0, 0, 0.28);
}

.avatar-level-chip {
  position: absolute;
  top: auto;
  bottom: -10px;
  left: 50%;
  z-index: 5;
  margin-top: 0;
  transform: translateX(-50%);
  padding: 5px 11px;
  border-radius: 999px;
  border: 1px solid rgba(188, 205, 244, 0.36);
  background: linear-gradient(180deg, rgba(14, 18, 30, 0.98), rgba(12, 15, 24, 0.96));
  color: #eef3ff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.04em;
  box-shadow:
    0 10px 22px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

.profile-user-card--restructured .display-name {
  font-size: 38px;
  line-height: 0.96;
  letter-spacing: -0.04em;
}

.profile-user-card--restructured .user-handle {
  margin-top: 4px;
  font-size: 15px;
  color: #9eaccd;
}

.profile-user-card--restructured .connection-row {
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid rgba(108, 126, 184, 0.4);
  background: linear-gradient(135deg, rgba(29, 36, 58, 0.92), rgba(23, 28, 44, 0.84));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 10px 24px rgba(0, 0, 0, 0.12);
  font-size: 13px;
  font-weight: 700;
}

.profile-user-card--restructured .connection-row i {
  color: #8ca5ff;
}

.profile-rank-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 96px;
  padding: 16px 18px;
  border-radius: 20px;
  border: 1px solid rgba(115, 129, 165, 0.32);
  background:
    linear-gradient(145deg, rgba(35, 39, 53, 0.9), rgba(26, 30, 42, 0.94)) padding-box,
    linear-gradient(135deg, rgba(129, 146, 197, 0.24), rgba(139, 94, 194, 0.2)) border-box;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 16px 34px rgba(0, 0, 0, 0.24);
  overflow: hidden;
}

.profile-rank-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 78% 36%, rgba(255, 255, 255, 0.06), transparent 28%);
  pointer-events: none;
}

.profile-rank-card__orb {
  width: 58px;
  height: 58px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  color: #e6eeff;
  background: linear-gradient(180deg, rgba(20, 24, 36, 0.98), rgba(12, 15, 23, 0.94));
  border: 1px solid rgba(188, 205, 244, 0.44);
  box-shadow: 0 0 0 4px rgba(108, 124, 184, 0.12);
}

.profile-rank-card__orb i {
  font-size: 21px;
}

.profile-rank-card__body {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.profile-rank-card__kicker {
  color: #8d98b5;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.profile-rank-card__title {
  color: #f6f8ff;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.05;
}

.profile-rank-card__meta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #9da8c4;
  font-size: 11px;
  font-weight: 600;
}

.profile-rank-card--hero {
  --badge-accent: #d9e5ff;
  --badge-bg: rgba(97, 106, 139, 0.24);
  --badge-border: rgba(149, 160, 196, 0.45);
  --badge-glow: rgba(139, 150, 187, 0.18);
  --badge-sheen: rgba(185, 196, 230, 0.22);
  align-self: start;
  justify-self: end;
  align-items: center;
  width: min(100%, 228px);
  min-height: 88px;
  transform: translateY(20px);
  padding: 9px 12px;
  gap: 10px;
  background:
    radial-gradient(circle at 82% 18%, var(--badge-glow), transparent 32%),
    linear-gradient(145deg, rgba(35, 39, 53, 0.94), rgba(26, 30, 42, 0.96)) padding-box,
    linear-gradient(135deg, var(--badge-border), var(--badge-sheen)) border-box;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 16px 34px rgba(0, 0, 0, 0.24),
    0 0 24px var(--badge-glow);
}

.profile-rank-card--hero::before {
  background: radial-gradient(circle at 78% 36%, rgba(255, 255, 255, 0.06), transparent 28%);
}

.profile-rank-card--hero .profile-rank-card__orb {
  width: 50px;
  height: 50px;
  border-radius: 16px;
  font-size: 18px;
  color: var(--badge-accent);
  background:
    radial-gradient(circle at 30% 25%, var(--badge-sheen), transparent 52%),
    linear-gradient(180deg, rgba(20, 24, 36, 0.98), rgba(12, 15, 23, 0.94));
  border-color: var(--badge-border);
  box-shadow:
    0 0 0 4px var(--badge-bg),
    0 0 22px var(--badge-glow);
}

.profile-rank-card--hero .profile-rank-card__body {
  justify-content: center;
  gap: 4px;
}

.profile-rank-card--hero .profile-rank-card__title {
  font-size: 17px;
  line-height: 1.02;
  color: #f7f9ff;
}

.profile-rank-card--hero .profile-rank-card__meta {
  font-size: 11px;
  line-height: 1;
  color: var(--badge-accent);
  opacity: 0.82;
}

.profile-hero-badges {
  grid-column: 1;
  width: 146px;
  align-self: start;
  margin-top: -100px;
}

.profile-hero-side .profile-rank-card--hero {
  grid-column: 2;
}

.profile-badge-list--hero {
  gap: 8px;
}

.profile-badge-card--hero {
  min-height: 58px;
  padding: 8px 10px;
  border-radius: 14px;
  gap: 8px;
}

.profile-badge-card--hero .profile-badge-card__orb {
  width: 32px;
  height: 32px;
  border-radius: 10px;
}

.profile-badge-card--hero .profile-badge-card__name {
  font-size: 11px;
  line-height: 1.1;
}

.profile-badge-card--hero .profile-badge-card__meta {
  font-size: 9px;
  line-height: 1.15;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.profile-rank-card__dots {
  display: inline-flex;
  gap: 4px;
  align-items: center;
}

.profile-rank-card__dots i {
  width: 10px;
  height: 4px;
  border-radius: 999px;
  background: linear-gradient(90deg, #8aa1ff, #c38cff);
  opacity: 0.88;
}

.profile-hero-divider {
  grid-area: divider;
  height: 1px;
  margin: 20px 0 18px;
  background: linear-gradient(90deg, rgba(105, 118, 158, 0.08), rgba(105, 118, 158, 0.28), rgba(105, 118, 158, 0.08));
}

.profile-hero-bottom {
  grid-area: bottom;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 18px;
  align-items: center;
  width: 100%;
}

.profile-inline-progress--hero {
  position: relative;
  top: auto;
  right: auto;
  width: 100%;
  align-self: end;
  margin: 0;
  padding: 0;
  background: transparent;
  border: 0;
  box-shadow: none;
}

.profile-inline-progress--hero .profile-inline-progress-head {
  align-items: center;
  margin-bottom: 12px;
}

.profile-inline-progress--hero .stat-number {
  font-size: 18px;
  line-height: 1;
}

.profile-inline-progress--hero .xp-meta {
  text-align: right;
  font-size: 14px;
  color: #8d98b6;
}

.profile-inline-progress--hero .xp-bar {
  height: 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.04);
}

.profile-inline-progress--hero .xp-fill {
  background: linear-gradient(90deg, #6187ff 0%, #8b7dff 52%, #c56dff 100%);
  box-shadow: 0 0 20px rgba(165, 121, 255, 0.25);
}

.profile-inline-progress--hero .xp-marker {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(231, 239, 255, 0.92);
}

.profile-inline-progress--hero .xp-milestone {
  top: 50%;
}

.cta-row--hero {
  width: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
  justify-content: flex-end;
  margin-left: auto;
  align-self: center;
  padding-top: 0;
}

.cta-action--wallet {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  height: 42px;
  padding-inline: 16px;
  border-radius: 13px;
  font-size: 12px;
  font-weight: 700;
  box-shadow: 0 10px 24px rgba(78, 115, 223, 0.3);
  white-space: nowrap;
}

.cta-icon-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 0;
}

.profile-user-card--restructured .cta-action--icon {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.045);
  border-color: rgba(255, 255, 255, 0.12);
}

.profile-user-card--restructured .cta-action--icon i {
  font-size: 16px;
}

.profile-progress-panel {
  grid-area: progress;
  align-self: center;
  background:
    linear-gradient(145deg, rgba(20, 24, 36, 0.8), rgba(16, 20, 32, 0.76)) padding-box,
    var(--profile-card-border) border-box;
  border: 1px solid transparent;
  border-radius: 18px;
  padding: 16px 20px;
  box-shadow: 0 22px 60px rgba(0, 0, 0, 0.45);
}

.progress-title {
  margin: 0 0 12px;
  color: #f6f8ff;
  font-size: 24px;
}

.profile-progress-panel .stat-card + .stat-card {
  margin-top: 12px;
}

.profile-performance-section {
  grid-area: performance;
}

.profile-achievements-section {
  grid-area: achievements;
}

.profile-recognition-section {
  grid-area: recognition;
}

.profile-layout .metrics-section,
.profile-layout .achievements-section {
  margin-top: 0;
}

.profile-hero {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 2.2fr) minmax(0, 1.3fr);
  gap: 24px;
  padding: 28px;
  border-radius: 32px;
  background: linear-gradient(145deg, rgba(20, 24, 36, 0.95), rgba(16, 18, 28, 0.92));
  border: 1px solid rgba(78, 115, 223, 0.18);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.05), inset 0 0 0 1px rgba(42, 47, 58, 0.7);
  overflow: hidden;
  transform: translateZ(0);
}

.profile-hero::before {
  content: '';
  position: absolute;
  inset: -1px;
  z-index: -1;
  background: radial-gradient(circle at 18% 45%, rgba(78, 115, 223, 0.28), transparent 52%),
    radial-gradient(circle at 82% 35%, rgba(155, 89, 182, 0.26), transparent 58%);
  filter: blur(35px);
  opacity: 0.5;
  animation: heroPulse 9s ease-in-out infinite alternate;
  pointer-events: none;
}

.profile-hero::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 24% 30%, rgba(78, 115, 223, 0.22), transparent 52%),
    radial-gradient(circle at 86% 60%, rgba(155, 89, 182, 0.14), transparent 60%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.03), transparent 55%);
  opacity: 0.45;
  filter: blur(38px);
  animation: heroFlow 16s ease-in-out infinite alternate;
  pointer-events: none;
}

.hero-left {
  display: grid;
  align-items: center;
}

.identity {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr) minmax(280px, 340px);
  gap: 22px;
  align-items: stretch;
  width: 100%;
}

.avatar-stack {
  display: flex;
  width: 100%;
  min-height: 100%;
  gap: 10px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin: 0;
  align-self: center;
  position: relative;
}

.avatar-ring {
  width: 184px;
  height: 184px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  position: relative;
  padding: 0;
  background: transparent;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.56);
  transition: transform 0.35s ease;
  overflow: visible;
  isolation: isolate;
}

.avatar-ring:hover {
  transform: translateY(-4px) scale(1.02);
}

.avatar-ring::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  z-index: 0;
  background: var(--profile-ring-gradient);
  filter: drop-shadow(0 0 10px rgba(111, 124, 236, 0.32));
}

.avatar-ring::after {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: 50%;
  z-index: 2;
  background: conic-gradient(
    from 0deg,
    rgba(78, 115, 223, 0) 0deg,
    rgba(78, 115, 223, 0) 282deg,
    rgba(78, 115, 223, 0.38) 300deg,
    rgba(78, 115, 223, 0.82) 314deg,
    rgba(126, 102, 228, 0.96) 328deg,
    rgba(155, 89, 182, 0.92) 342deg,
    rgba(126, 102, 228, 0.6) 352deg,
    rgba(78, 115, 223, 0) 360deg
  );
  animation: ringSweep 2.8s linear infinite;
  filter: drop-shadow(0 0 12px rgba(121, 118, 236, 0.5));
  mix-blend-mode: screen;
  pointer-events: none;
}

.avatar-shell {
  width: calc(100% - 10px);
  height: calc(100% - 10px);
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  z-index: 3;
  border: none;
  box-shadow: none;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.avatar-fallback {
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, rgba(78, 115, 223, 0.2), rgba(155, 89, 182, 0.18));
  color: #f6f8ff;
  font-weight: 700;
  font-size: 28px;
  letter-spacing: 1px;
}

.avatar-overlay {
  position: absolute;
  inset: 0;
  background: rgba(12, 15, 24, 0.18);
  display: grid;
  place-items: center;
  color: #d8eeff;
  opacity: 0;
  transition: opacity 0.25s ease;
}

.avatar-stack:hover .avatar-overlay {
  opacity: 1;
}

.avatar-save-btn {
  position: absolute;
  bottom: -12px;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, rgba(78, 115, 223, 0.95), rgba(155, 89, 182, 0.9));
  color: #f5f8ff;
  display: inline-grid;
  place-items: center;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(32, 55, 124, 0.36);
}

.avatar-save-btn:hover {
  transform: translateY(-1px);
}

.status-pill {
  justify-content: center;
  padding: 12px 18px;
  border-radius: 14px;
  background: rgba(95, 39, 205, 0.22);
  border: 1px solid rgba(155, 89, 182, 0.55);
  color: #c8c9ff;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  pointer-events: none;
  cursor: default;
}

.status-pill.connected {
  background: rgba(78, 115, 223, 0.18);
  border-color: rgba(78, 115, 223, 0.55);
  color: #cfe0ff;
}

.status-pill:hover {
  background: rgba(95, 39, 205, 0.22);
  border-color: rgba(155, 89, 182, 0.55);
  color: #c8c9ff;
}

.status-pill.connected:hover {
  background: rgba(78, 115, 223, 0.18);
  border-color: rgba(78, 115, 223, 0.55);
  color: #cfe0ff;
}

.identity-main {
  display: grid;
  align-content: center;
  gap: 10px;
  min-width: 0;
  max-width: none;
  padding-top: 4px;
  padding-right: 450px;
}

.profile-inline-progress {
  position: absolute;
  top: 28px;
  right: 28px;
  width: min(440px, calc(100% - 340px));
  align-self: center;
  margin-top: 0;
  padding: 14px 16px 14px;
  border-radius: 20px;
  background:
    linear-gradient(145deg, rgba(18, 23, 38, 0.78), rgba(12, 16, 28, 0.74)) padding-box,
    linear-gradient(135deg, rgba(116, 138, 212, 0.2), rgba(155, 89, 182, 0.16)) border-box;
  border: 1px solid transparent;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 16px 34px rgba(0, 0, 0, 0.24);
}

.profile-user-card--restructured .profile-inline-progress.profile-inline-progress--hero {
  position: relative;
  top: auto;
  right: auto;
  width: 100%;
  padding: 0;
  margin: 0;
  background: transparent;
  border: 0;
  box-shadow: none;
}

.profile-inline-progress-kicker {
  color: #8fa3d9;
  font-size: 10px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-weight: 700;
}

.profile-inline-progress-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.profile-inline-progress-copy {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.name-row {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
}

.name-block {
  display: grid;
  gap: 2px;
  min-width: 0;
  flex: 0 0 auto;
  width: fit-content;
  max-width: 340px;
}

.name-actions {
  display: flex;
  gap: 0;
  align-items: center;
  align-self: flex-start;
  justify-self: start;
  margin-top: 4px;
}

.display-name {
  margin: 0;
  font-size: 44px;
  font-weight: 800;
  letter-spacing: -0.5px;
  color: #f6f8ff;
  line-height: 0.98;
}

.user-handle {
  margin: 0;
  color: #a8b5d9;
  font-size: 16px;
  letter-spacing: 0.02em;
}

.display-input {
  font-size: 30px;
  line-height: 1;
  height: 46px;
  padding: 8px 12px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.35);
  color: #f6f8ff;
  outline: none;
  width: 100%;
  min-width: 240px;
  max-width: 340px;
}

.input-error {
  margin: 6px 0 0;
  font-size: 14px;
  color: #ff7b7b;
  line-height: 1.2;
}

.icon-button {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #dfe6ff;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.icon-button:hover {
  transform: translateY(-1px);
  border-color: transparent;
  background:
    linear-gradient(120deg, rgba(18, 24, 48, 0.9), rgba(28, 22, 51, 0.9)) padding-box,
    linear-gradient(120deg, rgba(78, 115, 223, 0.95), rgba(155, 89, 182, 0.95)) border-box;
  color: #ffffff;
  box-shadow: 0 8px 18px rgba(91, 112, 210, 0.35);
}

.tag-row { display: none; }

.level-band {
  width: min(560px, 100%);
  margin-top: 4px;
  border-radius: 14px;
  padding: 10px 14px;
  background: linear-gradient(90deg, rgba(26, 37, 75, 0.92), rgba(22, 27, 51, 0.78));
  border: 1px solid rgba(116, 138, 212, 0.34);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.level-band--compact {
  width: fit-content;
  margin: 0;
  padding: 6px 12px;
  border-radius: 10px;
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transform: translateY(-1px);
}

.level-band--compact .level-band__text {
  font-size: 12px;
  font-weight: 700;
  gap: 6px;
  white-space: nowrap;
  line-height: 1;
}

.level-band--bronze {
  background: linear-gradient(90deg, rgba(95, 67, 42, 0.38), rgba(121, 86, 56, 0.2));
  border-color: rgba(204, 151, 99, 0.45);
}

.level-band--rookie {
  background: linear-gradient(90deg, rgba(57, 66, 90, 0.42), rgba(72, 86, 118, 0.24));
  border-color: rgba(156, 170, 202, 0.42);
}

.level-band--rookie .level-band__text {
  color: #d2ddf5;
}

.level-band--bronze .level-band__text {
  color: #f0c798;
}

.level-band--silver {
  background: linear-gradient(90deg, rgba(77, 95, 129, 0.4), rgba(112, 136, 179, 0.2));
  border-color: rgba(166, 191, 229, 0.48);
}

.level-band--silver .level-band__text {
  color: #d8e5ff;
}

.level-band--gold {
  background: linear-gradient(90deg, rgba(116, 88, 35, 0.42), rgba(171, 130, 43, 0.22));
  border-color: rgba(247, 201, 88, 0.5);
}

.level-band--gold .level-band__text {
  color: #ffe199;
}

.level-band--ruby {
  background: linear-gradient(90deg, rgba(120, 40, 73, 0.45), rgba(172, 56, 112, 0.24));
  border-color: rgba(236, 109, 163, 0.52);
}

.level-band--ruby .level-band__text {
  color: #ffb7d6;
}

.level-band__text {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #f0c798;
  font-weight: 700;
  font-size: 20px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #e9ecf5;
  font-weight: 600;
  font-size: 14px;
}

.chip.level {
  background: linear-gradient(90deg, rgba(78, 115, 223, 0.2), rgba(78, 115, 223, 0.06));
  border-color: rgba(78, 115, 223, 0.35);
}

.chip.badge {
  background: linear-gradient(90deg, rgba(255, 184, 94, 0.14), rgba(255, 184, 94, 0.04));
  border-color: rgba(255, 184, 94, 0.32);
}

.chip.bronze {
  color: #ffcf96;
}

.chip.mini {
  font-size: 12px;
  opacity: 0.8;
}

.connection-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 2px;
}

.chip.connection {
  background: rgba(16, 20, 32, 0.65);
  border-color: rgba(112, 132, 196, 0.38);
  color: #c9d2eb;
}

.chip.connection.connected {
  border-color: rgba(90, 181, 138, 0.55);
  color: #d3ffe8;
}

.hero-quote {
  margin: 0;
  color: #c6cee5;
  font-size: 15px;
  max-width: 520px;
}

.identity-footer {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-top: 22px;
  width: 100%;
  margin-left: 0;
}

.cta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
  margin-top: 0;
  width: min(100%, 700px);
  margin-left: 0;
}

.insignias-panel {
  position: relative;
  width: min(320px, 100%);
  min-height: 0;
  border-radius: 22px;
  padding: 14px 14px 13px;
  margin-top: 0;
  border: 1px solid rgba(255, 255, 255, 0.07);
  background: #0b0d14;
  box-shadow: none;
  overflow: hidden;
}

.insignias-panel::before {
  content: '';
  position: absolute;
  inset: 0 0 auto;
  height: 3px;
  background: linear-gradient(120deg, #4e73df, #9b59b6);
  border-radius: 999px;
  z-index: 2;
}

.insignias-panel::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(160deg, rgba(78, 115, 223, 0.12), rgba(155, 89, 182, 0.15));
  pointer-events: none;
  opacity: 0.5;
  z-index: 0;
}

.insignias-list {
  display: grid;
  gap: 8px;
}

.insignias-panel > * {
  position: relative;
  z-index: 3;
}

.insignia-badge {
  --badge-accent: #9db3f1;
  --badge-bg: rgba(72, 86, 132, 0.24);
  --badge-border: rgba(127, 145, 198, 0.42);
  --badge-glow: rgba(123, 141, 199, 0.18);
  --badge-sheen: rgba(157, 179, 241, 0.2);
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  border-radius: 16px;
  padding: 9px 12px;
  background: linear-gradient(135deg, var(--badge-bg), rgba(9, 12, 22, 0.35));
  border: 1px solid var(--badge-border);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 8px 18px rgba(0, 0, 0, 0.22);
  overflow: hidden;
}

.insignia-badge::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(120deg, transparent 25%, var(--badge-sheen) 50%, transparent 75%);
  transform: translateX(-130%);
  animation: badgeSheen 5.8s ease-in-out infinite;
  pointer-events: none;
}

.insignia-badge__orb {
  width: 34px;
  height: 34px;
  border-radius: 11px;
  display: inline-grid;
  place-items: center;
  color: var(--badge-accent);
  background: rgba(9, 12, 22, 0.62);
  border: 1px solid var(--badge-border);
  box-shadow: 0 0 0 3px var(--badge-glow);
  flex-shrink: 0;
}

.insignia-badge__orb i {
  font-size: 14px;
}

.insignia-badge__body {
  display: grid;
  min-width: 0;
}

.insignia-badge__name {
  color: #e6ecff;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.01em;
  line-height: 1.1;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.insignia-badge__meta {
  color: rgba(179, 194, 230, 0.86);
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  line-height: 1.2;
  margin-top: 4px;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.insignia-badge--beta {
  --badge-accent: #b6b7ff;
  --badge-bg: linear-gradient(135deg, rgba(78, 115, 223, 0.28), rgba(155, 89, 182, 0.26));
  --badge-border: rgba(151, 126, 230, 0.56);
  --badge-glow: rgba(126, 113, 225, 0.24);
  --badge-sheen: rgba(170, 150, 245, 0.26);
}

.insignia-badge--discord {
  --badge-accent: #d8e5ff;
  --badge-bg: linear-gradient(135deg, rgba(70, 98, 202, 0.28), rgba(98, 122, 236, 0.22));
  --badge-border: rgba(115, 146, 255, 0.5);
  --badge-glow: rgba(88, 116, 228, 0.22);
  --badge-sheen: rgba(197, 214, 255, 0.22);
}

.insignia-badge--rookie {
  --badge-accent: #c4cde4;
  --badge-bg: rgba(97, 106, 139, 0.24);
  --badge-border: rgba(149, 160, 196, 0.45);
  --badge-glow: rgba(139, 150, 187, 0.18);
  --badge-sheen: rgba(185, 196, 230, 0.22);
}

.insignia-badge--bronze {
  --badge-accent: #dab38f;
  --badge-bg: rgba(122, 86, 63, 0.26);
  --badge-border: rgba(184, 133, 98, 0.5);
  --badge-glow: rgba(169, 121, 89, 0.2);
  --badge-sheen: rgba(218, 179, 143, 0.24);
}

.insignia-badge--silver {
  --badge-accent: #d9e5ff;
  --badge-bg: rgba(88, 109, 145, 0.24);
  --badge-border: rgba(157, 178, 216, 0.5);
  --badge-glow: rgba(141, 163, 203, 0.2);
  --badge-sheen: rgba(206, 223, 255, 0.26);
}

.insignia-badge--gold {
  --badge-accent: #e9c78f;
  --badge-bg: rgba(130, 103, 57, 0.26);
  --badge-border: rgba(196, 156, 87, 0.52);
  --badge-glow: rgba(188, 150, 86, 0.2);
  --badge-sheen: rgba(233, 199, 143, 0.28);
}

.insignia-badge--ruby {
  --badge-accent: #e4b1c8;
  --badge-bg: rgba(109, 56, 83, 0.28);
  --badge-border: rgba(177, 97, 132, 0.52);
  --badge-glow: rgba(164, 89, 122, 0.22);
  --badge-sheen: rgba(228, 177, 200, 0.26);
}

.insignia-badge--locked {
  --badge-accent: #a6adc2;
  --badge-bg: rgba(70, 78, 104, 0.2);
  --badge-border: rgba(126, 136, 166, 0.42);
  --badge-glow: rgba(118, 128, 159, 0.18);
  --badge-sheen: rgba(168, 176, 205, 0.18);
  border-style: dashed;
  filter: saturate(0.42) brightness(0.82) contrast(0.9);
}

.insignia-badge--locked::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    repeating-linear-gradient(
      135deg,
      rgba(190, 200, 228, 0.08) 0px,
      rgba(190, 200, 228, 0.08) 6px,
      rgba(190, 200, 228, 0.02) 6px,
      rgba(190, 200, 228, 0.02) 12px
    ),
    linear-gradient(180deg, rgba(8, 11, 20, 0.36), rgba(8, 11, 20, 0.5));
  pointer-events: none;
}

.insignia-badge--locked .insignia-badge__orb {
  background: rgba(9, 12, 22, 0.78);
  border-color: rgba(144, 153, 182, 0.5);
  box-shadow: inset 0 0 0 1px rgba(205, 214, 236, 0.12);
}

.insignia-badge--locked .insignia-badge__name,
.insignia-badge--locked .insignia-badge__meta {
  color: rgba(186, 196, 222, 0.78);
}

@keyframes badgeSheen {
  0% { transform: translateX(-130%); }
  30% { transform: translateX(130%); }
  100% { transform: translateX(130%); }
}

.btn-primary,
.btn-secondary {
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 12px 20px;
  border-radius: 9999px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: transform 0.18s ease-out, box-shadow 0.18s ease-out, background 0.18s ease-out, border-color 0.18s ease-out, color 0.18s ease-out;
  font-size: 19px;
  white-space: nowrap;
}

.btn-primary {
  background: linear-gradient(135deg, #5f27cd 0%, #3498db 100%);
  color: #ffffff;
  box-shadow: none;
  border: none;
  position: relative;
  transition: background 0.3s ease, transform 0.3s ease;
}

.btn-primary:hover {
  transform: translateY(-3px);
  background: linear-gradient(135deg, #4e73df 0%, #9b59b6 100%);
  box-shadow: none;
}

.btn-secondary {
  background: transparent;
  border: 1px solid rgba(78, 115, 223, 0.6);
  color: #cfe0ff;
}

.cta-action {
  min-height: 52px;
  min-width: 230px;
  text-transform: uppercase;
  letter-spacing: 0;
  font-weight: 700;
  font-size: 16px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.cta-action i {
  font-size: 18px;
}

.cta-action--icon {
  width: 52px;
  min-width: 52px;
  height: 52px;
  padding: 0;
  border-radius: 14px;
}

.cta-row .btn-primary.cta-action {
  width: 100%;
}

.cta-action--icon i {
  font-size: 20px;
}

.btn-secondary:hover {
  transform: translateY(-1px);
  background: rgba(78, 115, 223, 0.12);
}

.ghost-save-btn {
  padding-inline: 16px;
  padding-block: 10px;
  border-radius: 10px;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  background: transparent;
  border-color: rgba(78, 115, 223, 0.4);
  color: #cfe0ff;
}

.ghost-save-btn:hover {
  background: rgba(78, 115, 223, 0.1);
}

.btn-secondary.danger {
  border-color: rgba(255, 117, 117, 0.7);
  color: #ffb1b1;
  background: rgba(110, 21, 30, 0.35);
}

.hero-right {
  display: grid;
  gap: 12px;
}

.stat-card {
  background:
    linear-gradient(145deg, rgba(18, 20, 29, 0.94), rgba(16, 18, 26, 0.92)) padding-box,
    var(--profile-card-border) border-box;
  border: 1px solid transparent;
  border-radius: 12px;
  padding: 16px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04), 0 12px 30px rgba(0, 0, 0, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
}

.progress-header {
  display: none;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(78, 115, 223, 0.18);
  color: #dbe6ff;
  font-weight: 700;
  font-size: 12px;
}

.pill.ghost {
  background: rgba(255, 255, 255, 0.04);
  color: #b5bcc9;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.stat-number {
  font-size: 22px;
  font-weight: 800;
  color: #e6e8ec;
  margin: 0;
}

.stat-sub {
  margin: 8px 0 0;
  color: #7d8596;
  font-size: 13px;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.streak-strip {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 4px;
}

.streak-slot {
  width: 18px;
  height: 12px;
  border-radius: 999px;
  background: #11141c;
  border: 1px solid #2a2f3a;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.streak-slot.active {
  background: linear-gradient(120deg, rgba(78, 115, 223, 0.95), rgba(155, 89, 182, 0.92));
  border-color: rgba(126, 106, 229, 0.7);
}

.streak-slot.hot {
  box-shadow: 0 0 12px rgba(126, 106, 229, 0.45);
  transform: translateY(-1px);
}

.streak-hint {
  color: #9aa3b2;
  font-size: 12px;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.label {
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-size: 12px;
  color: #9aa3b2;
  margin: 0;
}

.xp-bar {
  position: relative;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 999px;
  height: 12px;
  overflow: visible;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.xp-main {
  margin-bottom: 10px;
}

.xp-headline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
}

.xp-headline .stat-number {
  margin: 0;
  line-height: 1;
}

.xp-meta-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
}

.xp-meta {
  font-size: 12px;
  color: #9aa3b2;
  margin-top: 2px;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.xp-remaining {
  font-weight: 700;
  background: linear-gradient(120deg, rgba(78, 115, 223, 0.98), rgba(155, 89, 182, 0.98));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.xp-fill {
  z-index: 1;
  position: relative;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    #5f27cd 0%,
    #4e73df 35%,
    #3498db 70%,
    #9b59b6 100%
  );
  overflow: hidden;
  transition: width 0.35s ease;
  animation: xpPulse 2.6s ease-in-out infinite;
}

.xp-fill::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(
    110deg,
    rgba(255, 255, 255, 0) 18%,
    rgba(255, 255, 255, 0.34) 48%,
    rgba(255, 255, 255, 0) 76%
  );
  transform: translateX(-140%);
  animation: xpShimmer 3.2s ease-in-out infinite;
  pointer-events: none;
}

.xp-marker {
  z-index: 2;
  position: absolute;
  top: 50%;
  width: 12px;
  height: 12px;
  background: #0c0f18;
  border: 2px solid #7394f0;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 0 6px rgba(78, 115, 223, 0.18), 0 0 18px rgba(78, 115, 223, 0.5);
}

.xp-marker::after {
  content: '';
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  border: 2px solid rgba(78, 115, 223, 0.35);
  animation: ping 2.2s ease-out infinite;
}

.xp-milestone {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  display: grid;
  place-items: center;
  gap: 2px;
}

.xp-m-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.7);
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
}

.xp-m-text {
  position: absolute;
  top: -14px;
  color: #9aa3b2;
  font-size: 10px;
  white-space: nowrap;
}

.xp-dot {
  position: static;
  color: #cdd5f0;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  margin-left: auto;
  white-space: nowrap;
}

.micro-kpis {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.micro-kpi {
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #2a2f3a;
  background: linear-gradient(135deg, rgba(18, 20, 29, 0.9), rgba(14, 17, 26, 0.9));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05), 0 6px 18px rgba(0, 0, 0, 0.35);
  min-width: 130px;
}

.micro-label {
  color: #9aa3b2;
  font-size: 12px;
  margin: 0 0 4px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.micro-value {
  color: #e6e8ec;
  font-weight: 800;
  font-size: 18px;
}

.xp-notes {
  display: flex;
  justify-content: space-between;
  color: #7d8596;
  font-size: 13px;
  margin-top: 8px;
}

.metrics-section,
.achievements-section {
  background:
    linear-gradient(145deg, rgba(20, 24, 36, 0.8), rgba(16, 20, 32, 0.76)) padding-box,
    var(--profile-card-border) border-box;
  border: 1px solid transparent;
  border-radius: 18px;
  padding: 20px 24px;
  box-shadow: 0 22px 60px rgba(0, 0, 0, 0.45);
  margin-top: 18px;
}

.section-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}

.section-heading h2 {
  margin: 2px 0 0;
  color: #f6f8ff;
  font-size: 20px;
}

.section-kicker {
  margin: 0 0 4px;
  color: #8d98b5;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}

.section-pill {
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid rgba(133, 146, 182, 0.26);
  background: rgba(255, 255, 255, 0.04);
  color: #c8d2ec;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

.achievements-title {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.01em;
  text-transform: none;
  color: #f6f8ff;
  text-shadow: none;
}

.profile-dashboard-title {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.01em;
  text-transform: none;
  color: #f6f8ff;
  background: none;
  -webkit-background-clip: border-box;
  background-clip: border-box;
  text-shadow: none;
}

.profile-dashboard-title--light {
  color: #f6f8ff;
}

.profile-achievements-section {
  padding-top: 14px;
}

.profile-performance-section {
  padding-top: 14px;
}

.profile-recognition-section {
  padding-top: 14px;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.metric-card {
  background:
    linear-gradient(145deg, rgba(21, 26, 36, 0.95), rgba(17, 22, 32, 0.94)) padding-box,
    var(--profile-card-border) border-box;
  border: 1px solid transparent;
  border-radius: 12px;
  padding: 10px 14px 12px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  min-height: 72px;
  display: grid;
  align-content: start;
  gap: 6px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.metric-card:hover,
.achievement-card:hover {
  transform: translateY(-2px);
}

.metric-label {
  color: #9aa3b2;
  font-size: 12px;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.metric-value {
  font-size: 22px;
  font-weight: 800;
  color: #e6e8ec;
}

.pnl-spotlight-card {
  margin-top: 12px;
  border-radius: 14px;
  padding: 16px 18px 14px;
  min-height: 190px;
  position: relative;
  overflow: hidden;
  background:
    linear-gradient(145deg, rgba(17, 22, 33, 0.96), rgba(13, 18, 29, 0.95)) padding-box,
    var(--profile-card-border) border-box;
  border: 1px solid transparent;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 14px 30px rgba(10, 15, 28, 0.45);
}

.pnl-spotlight-card::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(circle at 80% 24%, rgba(78, 115, 223, 0.18), transparent 46%),
    radial-gradient(circle at 24% 74%, rgba(155, 89, 182, 0.14), transparent 42%);
}

.pnl-label {
  position: relative;
  margin-top: 2px;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.08em;
  color: #b4bfd7;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.pnl-value {
  position: relative;
  margin-top: 6px;
  font-size: 44px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: -0.02em;
  background: linear-gradient(120deg, #f4f7ff 8%, #b7c9ff 48%, #cfb7ff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.pnl-value.value-pos {
  background: linear-gradient(120deg, #d4ffe9 8%, #7bf1c3 54%, #44dba0 100%);
  -webkit-background-clip: text;
  background-clip: text;
}

.pnl-value.value-neg {
  background: linear-gradient(120deg, #ffe0e4 8%, #ff9fb0 54%, #f26d83 100%);
  -webkit-background-clip: text;
  background-clip: text;
}

.pnl-chart {
  position: relative;
  margin-top: 8px;
  height: 156px;
  padding: 10px 10px 8px 0;
  box-sizing: border-box;
}

.pnl-mini-chart {
  display: grid;
  grid-template-columns: 38px 1fr;
  gap: 8px;
  align-items: stretch;
  width: 100%;
  height: 100%;
  border: 1px solid rgba(138, 149, 178, 0.22);
  border-radius: 12px;
  padding: 8px 10px 8px 6px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.025), rgba(255, 255, 255, 0));
}

.pnl-mini-chart__y-axis {
  height: 100%;
  color: #7f8aa1;
  font-size: 10px;
}

.pnl-mini-chart__canvas {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.axis-svg {
  width: 100%;
  height: 100%;
  display: block;
}

.axis-text {
  fill: rgba(169, 183, 220, 0.9);
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.pnl-line-svg {
  width: 100%;
  height: 126px;
  display: block;
  overflow: visible;
  border-radius: 8px;
}

.pnl-grid-line {
  stroke: rgba(169, 183, 220, 0.12);
  stroke-width: 1;
}

.pnl-grid-zero {
  stroke: rgba(169, 183, 220, 0.28);
  stroke-width: 1.15;
  stroke-dasharray: 4 4;
}

.pnl-area-positive {
  fill: url(#profilePnlAreaPositive);
}

.pnl-area-negative {
  fill: url(#profilePnlAreaNegative);
}

.pnl-line-positive {
  fill: none;
  stroke: #34c58f;
  stroke-width: 2.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.pnl-line-negative {
  fill: none;
  stroke: #f05465;
  stroke-width: 2.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.pnl-mini-chart__x-axis {
  color: rgba(169, 183, 220, 0.9);
  font-size: 11px;
  display: flex;
  justify-content: space-between;
  padding: 0 2px;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}

.pnl-dot {
  fill: #eef3ff;
  stroke: #8f9bff;
  stroke-width: 1.4;
  filter: drop-shadow(0 0 8px rgba(143, 155, 255, 0.95));
}

.pnl-dot-halo {
  fill: rgba(143, 155, 255, 0.3);
  filter: drop-shadow(0 0 10px rgba(143, 155, 255, 0.58));
  transform-origin: center;
  animation: pnlDotPulse 1.9s ease-in-out infinite;
}

@keyframes pnlDotPulse {
  0%,
  100% {
    opacity: 0.35;
  }
  50% {
    opacity: 0.85;
  }
}

.metric-hint {
  color: #7d8596;
  font-size: 12px;
}

.achievement-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
}

.achievement-card {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 12px;
  background:
    linear-gradient(145deg, rgba(21, 26, 36, 0.95), rgba(17, 22, 32, 0.94)) padding-box,
    var(--profile-card-border) border-box;
  border: 1px solid transparent;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  min-height: 64px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
}

.achievement-card.locked,
.achievement-card.is-locked {
  opacity: 0.84;
  filter: saturate(0.74) brightness(0.9);
  position: relative;
  overflow: hidden;
  background:
    linear-gradient(145deg, rgba(8, 11, 18, 0.98), rgba(13, 16, 25, 0.96)) padding-box,
    linear-gradient(140deg, rgba(92, 107, 150, 0.46), rgba(68, 79, 117, 0.34)) border-box;
  box-shadow:
    0 10px 24px rgba(0, 0, 0, 0.32),
    inset 0 0 0 1px rgba(90, 105, 146, 0.2);
}

.achievement-card.locked::before,
.achievement-card.is-locked::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(6, 9, 15, 0.62), rgba(6, 9, 15, 0.5)),
    repeating-linear-gradient(
      -55deg,
      rgba(130, 146, 198, 0.08) 0 9px,
      rgba(30, 37, 59, 0.06) 9px 18px
    );
  pointer-events: none;
  z-index: 1;
}

.achievement-card.locked > *,
.achievement-card.is-locked > * {
  position: relative;
  z-index: 2;
}

.achievement-card.unlocked,
.achievement-card.is-unlocked {
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 0 0 1px rgba(90, 181, 138, 0.22),
    0 12px 28px rgba(26, 46, 36, 0.24);
  background:
    radial-gradient(circle at 18% 14%, rgba(105, 233, 173, 0.12), transparent 38%),
    linear-gradient(145deg, rgba(22, 28, 37, 0.97), rgba(16, 21, 29, 0.96)) padding-box,
    linear-gradient(140deg, rgba(120, 219, 171, 0.28), rgba(83, 132, 255, 0.16)) border-box;
}

.badge-icon {
  position: relative;
  width: 34px;
  height: 42px;
  display: grid;
  place-items: center;
  background: transparent;
  color: #d9e3ff;
  box-shadow: none;
}

.badge-icon i {
  font-size: 18px;
  text-shadow: 0 2px 8px rgba(63, 98, 201, 0.28);
}

.achievement-card.locked .badge-icon i,
.achievement-card.is-locked .badge-icon i {
  color: #bec8e4;
  opacity: 0.9;
}

.achievement-card.is-locked .achievement-title,
.achievement-card.locked .achievement-title {
  color: #c4cce0;
}

.achievement-card.is-locked .achievement-meta,
.achievement-card.locked .achievement-meta {
  color: #8d96aa;
}

.achievement-card.is-locked .achievement-progress,
.achievement-card.locked .achievement-progress {
  color: #7d869d;
}

.achievement-title {
  margin: 0;
  font-weight: 700;
  color: #e6e8ec;
}

.achievement-info {
  min-width: 0;
  flex: 1;
}

.achievement-meta {
  margin: 2px 0 0;
  color: #7d8596;
  font-size: 12px;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.achievement-status {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.achievement-progress {
  font-size: 12px;
  font-weight: 700;
  color: #b8c2db;
  letter-spacing: 0.04em;
}

.achievement-state {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  display: inline-grid;
  place-items: center;
  border: 1px solid transparent;
}

.achievement-state i {
  font-size: 10px;
}

.achievement-state.pending {
  background: rgba(110, 120, 146, 0.16);
  border-color: rgba(145, 157, 188, 0.32);
  color: #9aa3b2;
}

.achievement-state.done {
  background: rgba(53, 190, 135, 0.2);
  border-color: rgba(90, 181, 138, 0.55);
  color: #9ff3ca;
}

.text-button {
  background: none;
  border: none;
  color: #7cf7ff;
  cursor: pointer;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(4, 10, 20, 0.72);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  position: relative;
  width: 400px;
  max-width: 90%;
  padding: 30px 35px;
  border-radius: 15px;
  border: 1px solid transparent;
  background:
    linear-gradient(160deg, rgba(18, 26, 44, 0.72), rgba(13, 19, 34, 0.78)) padding-box,
    linear-gradient(120deg, rgba(78, 115, 223, 0.9), rgba(155, 89, 182, 0.9)) border-box;
  backdrop-filter: blur(18px) saturate(120%);
  -webkit-backdrop-filter: blur(18px) saturate(120%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 0 0 1px rgba(78, 115, 223, 0.24),
    0 14px 30px rgba(4, 10, 24, 0.5),
    0 0 26px rgba(155, 89, 182, 0.2);
  text-align: center;
}

.modal-close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.85);
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.16);
  border-color: rgba(255, 255, 255, 0.4);
  color: #ffffff;
  transform: translateY(-1px);
}

.modal-close i {
  font-size: 12px;
}

.modal-content h2 {
  margin: 0 0 8px;
  color: #ffffff;
  font-weight: 600;
  font-size: 2rem;
}

.subtext {
  font-size: 0.95rem;
  margin-bottom: 18px;
  color: rgba(255, 255, 255, 0.78);
}

.modal-form {
  width: 100%;
}

.textfield {
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
  margin-bottom: 16px;
}

.textfield i {
  position: absolute;
  left: 15px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
}

.textfield input {
  flex: 1;
  border: none;
  border-radius: 10px;
  padding: 15px 16px 15px 50px;
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
  font-size: 12pt;
  outline: none;
  transition: background-color 0.3s ease;
}

.textfield input:focus {
  background: rgba(255, 255, 255, 0.24);
}

.textfield input::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.modal-actions {
  margin-top: 6px;
  display: grid;
  gap: 10px;
}

.btn-submit {
  width: 100%;
  padding: 12px 0;
  border-radius: 50px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.3s ease, transform 0.3s ease, border-color 0.2s ease;
}

.btn-submit {
  background: linear-gradient(135deg, #4e73df 0%, #9b59b6 100%);
  color: #ffffff;
  border: none;
}

.btn-submit:not(:disabled):hover {
  background: linear-gradient(135deg, #5f27cd 0%, #3498db 100%);
  transform: translateY(-3px);
}

.btn-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-submit.btn-danger {
  background: linear-gradient(135deg, #d95757 0%, #b54444 100%);
}

.btn-submit.btn-danger:not(:disabled):hover {
  background: linear-gradient(135deg, #e36464 0%, #c25151 100%);
}

.btn-cancel {
  width: 100%;
  padding: 12px 0;
  border-radius: 50px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  transition: background 0.3s ease, transform 0.3s ease, border-color 0.2s ease;
}

.btn-cancel:not(:disabled):hover {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.4);
  transform: translateY(-2px);
}

.btn-cancel:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

@keyframes pulse {
  0% { opacity: 0.5; transform: scale(0.98); }
  50% { opacity: 0.95; transform: scale(1.02); }
  100% { opacity: 0.6; transform: scale(1); }
}

@keyframes heroPulse {
  0% { opacity: 0.45; transform: scale(1); }
  100% { opacity: 0.6; transform: scale(1.05); }
}

@keyframes heroFlow {
  0% { transform: translate3d(-6px, 4px, 0); }
  100% { transform: translate3d(6px, -4px, 0); }
}

@keyframes bgDrift {
  0% { transform: translate3d(0,0,0); }
  100% { transform: translate3d(10px,-12px,0); }
}

@keyframes ringSweep {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes ping {
  0% { transform: scale(1); opacity: 0.6; }
  70% { transform: scale(1.8); opacity: 0; }
  100% { transform: scale(1.8); opacity: 0; }
}

@keyframes xpPulse {
  0% { filter: brightness(0.94) saturate(0.95); box-shadow: 0 0 0 rgba(92, 127, 240, 0); }
  50% { filter: brightness(1.08) saturate(1.08); box-shadow: 0 0 14px rgba(92, 127, 240, 0.34); }
  100% { filter: brightness(0.96) saturate(0.98); box-shadow: 0 0 0 rgba(92, 127, 240, 0); }
}

@keyframes xpShimmer {
  0% { transform: translateX(-140%); opacity: 0; }
  20% { opacity: 0.45; }
  55% { opacity: 0.7; }
  100% { transform: translateX(140%); opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .ring-glow,
  .xp-fill,
  .xp-fill::after,
  .avatar-ring::after,
  .profile-user-card::before,
  .profile-user-card::after,
  .xp-marker::after,
  .insignia-badge::after {
    animation: none !important;
  }
}

@media (max-width: 1199px) {
  .profile-page {
    padding: calc(var(--app-header-h) + 16px) var(--app-gutter) 32px;
  }
  .profile-layout {
    grid-template-columns: 1fr;
    grid-template-areas:
      'user'
      'performance'
      'recognition'
      'achievements';
  }
  .profile-user-card {
    padding: 20px;
  }
  .profile-hero-top,
  .profile-hero-bottom,
  .profile-hero-identity {
    grid-template-columns: 1fr;
  }
  .profile-hero-identity {
    justify-items: center;
    text-align: center;
  }
  .profile-user-card--restructured .connection-row,
  .cta-row--hero {
    justify-content: center;
  }
  .profile-rank-card {
    max-width: 420px;
    width: 100%;
    margin: 0 auto;
  }
  .profile-inline-progress--hero .profile-inline-progress-head {
    flex-direction: column;
    align-items: center;
  }
  .profile-inline-progress--hero .xp-meta {
    text-align: center;
  }
  .cta-row--hero {
    flex-wrap: wrap;
  }
  .cta-icon-row {
    justify-content: center;
    flex-wrap: wrap;
  }
  .name-actions {
    justify-content: center;
    align-self: center;
    margin-top: 0;
    justify-self: center;
  }
  .name-row {
    flex-direction: column;
    align-items: center;
  }
  .metrics-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .profile-page {
    padding: calc(var(--app-header-h) + 12px) var(--app-gutter) 28px;
  }
  .profile-user-card {
    padding: 16px;
  }
  .profile-user-card--restructured .display-name,
  .display-name {
    font-size: 32px;
  }
  .user-handle {
    font-size: 16px;
  }
  .profile-user-card--restructured .avatar-ring,
  .avatar-ring {
    width: 138px;
    height: 138px;
  }
  .level-band__text {
    font-size: 16px;
  }
  .profile-inline-progress {
    padding: 14px 14px 15px;
  }
  .btn-primary,
  .btn-secondary {
    font-size: 16px;
    padding: 12px 18px;
  }
  .chip {
    font-size: 13px;
  }
  .cta-action {
    min-width: 100%;
  }
  .cta-action--icon {
    width: 52px;
    min-width: 52px;
  }
  .pnl-chart {
    height: 138px;
  }
  .pnl-line-svg {
    height: 108px;
  }
  .metrics-grid {
    grid-template-columns: 1fr;
  }
  .cta-row--hero {
    flex-wrap: wrap;
  }
  .cta-action--wallet {
    min-width: 100%;
  }
  .cta-icon-row {
    width: 100%;
    justify-content: center;
  }
  .profile-rank-card {
    padding: 14px;
  }
  .section-pill {
    font-size: 11px;
    padding: 7px 10px;
  }
}

@media (min-width: 1200px) {
  .cta-row {
    width: min(100%, 700px);
    margin-left: 0;
  }
}

.insignias-panel--standalone {
  width: 100%;
  padding: 0;
  background: transparent;
  border: 0;
  box-shadow: none;
}

.insignias-panel--standalone::before,
.insignias-panel--standalone::after {
  display: none;
}

.profile-page {
  min-height: 100vh;
  padding: calc(var(--app-header-h) + 22px) var(--app-gutter) 34px;
  background:
    radial-gradient(circle at top left, rgba(78, 115, 223, 0.16), transparent 28%),
    radial-gradient(circle at 80% 12%, rgba(155, 89, 182, 0.12), transparent 24%),
    linear-gradient(180deg, #13161d 0%, #0f1116 100%);
}

.profile-page::before,
.profile-page::after {
  content: none;
  display: none;
}

.profile-layout--fidelity {
  width: min(1480px, 100%);
  margin: 0 auto;
  display: grid;
  grid-template-areas: none;
  grid-template-columns: minmax(0, 1fr);
  gap: 22px;
  align-items: start;
}

.profile-content-grid {
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1.66fr) minmax(320px, 0.84fr);
  gap: 22px;
  align-items: start;
}

.profile-main,
.profile-sidebar {
  display: grid;
  gap: 22px;
}

.profile-card {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  border: 1px solid rgba(122, 133, 163, 0.2);
  background:
    linear-gradient(145deg, rgba(20, 24, 36, 0.96), rgba(14, 18, 28, 0.98)) padding-box,
    linear-gradient(135deg, rgba(78, 115, 223, 0.34), rgba(155, 89, 182, 0.2)) border-box;
  box-shadow:
    0 22px 56px rgba(0, 0, 0, 0.44),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(18px) saturate(120%);
  -webkit-backdrop-filter: blur(18px) saturate(120%);
}

.profile-card::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(circle at 10% 12%, rgba(255, 255, 255, 0.05), transparent 28%),
    radial-gradient(circle at 84% 16%, rgba(78, 115, 223, 0.12), transparent 28%),
    radial-gradient(circle at 74% 82%, rgba(155, 89, 182, 0.1), transparent 30%);
}

.profile-hero-card {
  grid-column: 1 / -1;
  padding: 22px 22px 18px;
  overflow: visible;
}

.profile-hero-grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 414px;
  gap: 18px;
  align-items: center;
}

.profile-hero-identity {
  display: flex;
  align-items: flex-start;
  gap: 22px;
  min-width: 0;
}

.profile-hero-side {
  display: grid;
  grid-template-columns: 146px 250px;
  gap: 10px;
  align-items: start;
  align-self: start;
  justify-self: end;
  width: 406px;
}

.profile-avatar-wrap {
  position: relative;
  width: 140px;
  height: 140px;
  flex: 0 0 auto;
  cursor: pointer;
  padding-top: 14px;
}

.profile-avatar-frame {
  position: absolute;
  inset: 0;
  padding: 9px;
  border-radius: 50%;
  background: linear-gradient(145deg, rgba(78, 115, 223, 0.24), rgba(155, 89, 182, 0.18));
  box-shadow:
    0 18px 45px rgba(6, 10, 24, 0.42),
    0 0 0 1px rgba(255, 255, 255, 0.04),
    0 0 22px rgba(104, 126, 214, 0.12);
}

.profile-avatar-frame::before {
  content: '';
  position: absolute;
  inset: -5px;
  border-radius: 50%;
  background: conic-gradient(
    from 210deg,
    rgba(103, 132, 244, 0.08) 0deg,
    rgba(103, 132, 244, 0.18) 78deg,
    rgba(196, 213, 255, 0.88) 112deg,
    rgba(141, 168, 255, 0.98) 138deg,
    rgba(194, 126, 233, 0.9) 164deg,
    rgba(118, 146, 247, 0.22) 214deg,
    rgba(103, 132, 244, 0.08) 360deg
  );
  animation: avatarRingSpin 6.8s linear infinite;
  -webkit-mask: radial-gradient(circle, transparent 58%, #000 61%, #000 66%, transparent 69%);
  mask: radial-gradient(circle, transparent 58%, #000 61%, #000 66%, transparent 69%);
  filter:
    drop-shadow(0 0 8px rgba(154, 181, 255, 0.18))
    drop-shadow(0 0 14px rgba(118, 147, 247, 0.2));
  opacity: 0.98;
}

.profile-avatar-frame::after {
  content: '';
  position: absolute;
  inset: 5px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: inset 0 0 0 1px rgba(56, 71, 110, 0.16);
  pointer-events: none;
}

.profile-avatar-shell {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(145deg, #101521, #161c2b);
}

.profile-avatar-shell::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.08);
  pointer-events: none;
}

.profile-avatar-img,
.profile-avatar-fallback {
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  object-fit: cover;
}

.profile-avatar-fallback {
  background: linear-gradient(145deg, #171e30, #0f1320);
  color: #edf2ff;
  font: 800 54px/1 'Montserrat', 'Inter', sans-serif;
  letter-spacing: -0.04em;
}

.profile-avatar-overlay {
  position: absolute;
  inset: auto 0 0 0;
  height: 42px;
  display: grid;
  place-items: center;
  color: #f4f7ff;
  background: linear-gradient(180deg, rgba(10, 12, 18, 0), rgba(10, 12, 18, 0.74));
  opacity: 0;
  transition: opacity 0.18s ease;
}

.profile-avatar-wrap:hover .profile-avatar-overlay {
  opacity: 1;
}

.profile-avatar-save-btn {
  position: absolute;
  right: -4px;
  bottom: 2px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid rgba(48, 197, 141, 0.34);
  background: linear-gradient(135deg, #30c58d 0%, #19815f 100%);
  color: #ffffff;
  box-shadow: 0 14px 30px rgba(16, 96, 69, 0.4);
  cursor: pointer;
}

.profile-hero-copy {
  min-width: 0;
  flex: 1;
}

.profile-name-row {
  display: flex;
  align-items: flex-start;
}

.profile-name-block {
  display: grid;
  gap: 10px;
  min-width: 0;
}

.profile-name-line {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.profile-display-name,
.profile-display-input {
  margin: 0;
  color: #f6f8ff;
  font-family: 'Montserrat', 'Inter', sans-serif;
  font-size: clamp(34px, 4.1vw, 56px);
  font-weight: 800;
  line-height: 0.98;
  letter-spacing: -0.05em;
}

.profile-display-input {
  width: min(100%, 460px);
  border: 0;
  border-bottom: 1px solid rgba(122, 133, 163, 0.4);
  background: transparent;
  outline: none;
  padding: 0 0 8px;
  box-shadow: none;
}

.profile-name-edit-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid rgba(122, 133, 163, 0.24);
  background: rgba(255, 255, 255, 0.05);
  color: #dbe3f7;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease;
}

.profile-name-edit-btn:hover {
  transform: translateY(-1px);
  border-color: rgba(78, 115, 223, 0.35);
  background: rgba(78, 115, 223, 0.14);
}

.profile-user-handle {
  margin: 0;
  color: #95a1be;
  font-size: 16px;
  font-weight: 600;
}

.profile-discord-pill {
  --discord-pill-accent: rgba(112, 128, 180, 0.22);
  --discord-pill-edge: rgba(87, 98, 132, 0.22);
  display: inline-flex;
  align-items: center;
  gap: 12px;
  width: fit-content;
  min-height: 50px;
  margin: 0;
  padding: 7px 15px 7px 9px;
  border-radius: 0;
  clip-path: polygon(10px 0, calc(100% - 12px) 0, 100% 12px, 100% calc(100% - 12px), calc(100% - 10px) 100%, 12px 100%, 0 calc(100% - 12px), 0 10px);
  border: 1px solid transparent;
  background:
    linear-gradient(145deg, rgba(24, 29, 44, 0.97), rgba(15, 18, 29, 0.97)) padding-box,
    linear-gradient(135deg, var(--discord-pill-accent), var(--discord-pill-edge)) border-box;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 12px 24px rgba(7, 10, 18, 0.22);
  position: relative;
  overflow: hidden;
}

.profile-discord-pill::before {
  content: '';
  position: absolute;
  inset: 0;
  background: none;
  pointer-events: none;
}

.profile-discord-pill::after {
  content: '';
  position: absolute;
  inset: auto 10px 8px 56px;
  height: 1px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.16), transparent 82%);
  opacity: 0.4;
  pointer-events: none;
}

.profile-discord-pill__orb {
  position: relative;
  z-index: 1;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  background:
    radial-gradient(circle at 30% 25%, rgba(173, 188, 255, 0.22), transparent 48%),
    linear-gradient(180deg, rgba(31, 37, 57, 0.98), rgba(16, 20, 30, 0.96));
  border: 1px solid rgba(148, 161, 198, 0.24);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 8px 18px rgba(0, 0, 0, 0.2);
}

.profile-discord-pill__orb i {
  font-size: 14px;
}

.profile-discord-pill__body {
  position: relative;
  z-index: 1;
  display: grid;
  gap: 1px;
  min-width: 0;
}

.profile-discord-pill__label {
  color: #eef3ff;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  line-height: 1.1;
}

.profile-discord-pill__meta {
  color: #98a2bc;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.15;
}

.profile-discord-pill.is-connected {
  --discord-pill-accent: rgba(92, 108, 255, 0.24);
  --discord-pill-edge: rgba(126, 145, 255, 0.1);
  background:
    linear-gradient(145deg, rgba(28, 34, 54, 0.97), rgba(17, 21, 34, 0.96)) padding-box,
    linear-gradient(135deg, var(--discord-pill-accent), var(--discord-pill-edge)) border-box;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 10px 20px rgba(25, 35, 78, 0.16);
}

.profile-discord-pill.is-connected .profile-discord-pill__label::after {
  content: 'ONLINE';
  display: inline-block;
  margin-left: 6px;
  padding: 2px 5px;
  border-radius: 999px;
  background: rgba(88, 101, 242, 0.22);
  color: #dfe6ff;
  font-size: 8px;
  letter-spacing: 0.14em;
  vertical-align: middle;
}

.profile-discord-pill.is-connected .profile-discord-pill__orb {
  background:
    linear-gradient(180deg, rgba(36, 44, 74, 0.98), rgba(20, 25, 42, 0.98));
  border-color: rgba(122, 139, 255, 0.22);
  color: #eef3ff;
  box-shadow:
    0 0 0 2px rgba(88, 101, 242, 0.08);
}

.profile-discord-pill.is-connected .profile-discord-pill__meta {
  color: #aeb9d8;
}

.profile-discord-pill.is-idle {
  --discord-pill-accent: rgba(111, 122, 152, 0.24);
  --discord-pill-edge: rgba(72, 81, 106, 0.18);
  background:
    linear-gradient(180deg, rgba(9, 12, 19, 0.44), rgba(9, 12, 19, 0.34)),
    repeating-linear-gradient(
      -55deg,
      rgba(130, 146, 198, 0.06) 0 8px,
      rgba(26, 31, 47, 0.05) 8px 16px
    ),
    linear-gradient(145deg, rgba(24, 28, 41, 0.96), rgba(15, 18, 28, 0.96)) padding-box,
    linear-gradient(135deg, var(--discord-pill-accent), var(--discord-pill-edge)) border-box;
}

.profile-discord-pill.is-idle .profile-discord-pill__label::after {
  content: 'LOCKED';
  display: inline-block;
  margin-left: 6px;
  padding: 2px 5px;
  border-radius: 999px;
  background: rgba(160, 171, 201, 0.12);
  color: #d5dcee;
  font-size: 8px;
  letter-spacing: 0.14em;
  vertical-align: middle;
}

.profile-discord-pill.is-idle .profile-discord-pill__orb {
  color: #c6d0ea;
  background:
    linear-gradient(180deg, rgba(26, 31, 45, 0.98), rgba(16, 20, 30, 0.98));
  border-color: rgba(103, 114, 149, 0.2);
}

.profile-discord-pill.is-idle .profile-discord-pill__orb::after {
  content: '\f023';
  position: absolute;
  right: -4px;
  bottom: -4px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, rgba(17, 20, 31, 0.98), rgba(8, 10, 16, 0.98));
  border: 1px solid rgba(138, 152, 191, 0.22);
  color: #d7def2;
  font-family: 'Font Awesome 5 Free';
  font-size: 6px;
  font-weight: 900;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.32);
}

.profile-discord-pill.is-idle .profile-discord-pill__label {
  color: #dbe2f4;
}

.profile-discord-pill.is-idle .profile-discord-pill__meta {
  color: #96a1ba;
}

.profile-hero-footer {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 16px;
  align-items: end;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(138, 149, 178, 0.16);
}

.profile-xp-block {
  display: grid;
  gap: 10px;
  min-width: 0;
}

.profile-xp-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;
}

.profile-xp-copy {
  display: flex;
  align-items: baseline;
  min-width: 0;
}

.profile-xp-stat {
  white-space: nowrap;
  font-size: 19px;
  line-height: 1.05;
}

.profile-xp-meta {
  margin-left: auto;
  color: #92a0bf;
  font-size: 14px;
  line-height: 1.35;
  text-align: right;
  white-space: nowrap;
}

.profile-xp-bar {
  position: relative;
  height: 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(122, 133, 163, 0.18);
  overflow: visible;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.profile-xp-fill {
  position: absolute;
  inset: 0 auto 0 0;
  border-radius: inherit;
  background: linear-gradient(90deg, #4e73df 0%, #9b59b6 100%);
  box-shadow: 0 0 18px rgba(78, 115, 223, 0.28);
}

.profile-xp-marker {
  position: absolute;
  top: 50%;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
  background: #eef3ff;
  box-shadow:
    0 0 0 4px rgba(78, 115, 223, 0.18),
    0 0 18px rgba(78, 115, 223, 0.48);
}

.profile-xp-marker::after {
  content: '';
  position: absolute;
  inset: -6px;
  border-radius: inherit;
  border: 2px solid rgba(255, 255, 255, 0.14);
  animation: profileXpPulse 2.2s ease-out infinite;
}

.profile-xp-milestone {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
}

.profile-xp-m-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.68);
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
  display: block;
}

.profile-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.profile-action-btn {
  height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(122, 133, 163, 0.2);
  background: rgba(255, 255, 255, 0.05);
  color: #eff3ff;
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0 16px;
  cursor: pointer;
  transition: transform 0.18s ease, background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.22);
}

.profile-action-btn--primary {
  min-width: 224px;
  border-color: transparent;
  background:
    linear-gradient(135deg, #4e73df 0%, #9b59b6 100%) padding-box,
    linear-gradient(135deg, rgba(129, 160, 255, 0.72) 0%, rgba(197, 129, 240, 0.72) 100%) border-box;
  color: #ffffff;
  font-weight: 800;
  letter-spacing: 0.04em;
  box-shadow:
    0 14px 28px rgba(78, 115, 223, 0.18),
    0 12px 26px rgba(0, 0, 0, 0.22);
}

.profile-action-btn--icon {
  width: 44px;
  padding: 0;
}

.profile-action-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  border-color: rgba(160, 174, 210, 0.32);
  background: rgba(255, 255, 255, 0.08);
  box-shadow:
    0 18px 30px rgba(0, 0, 0, 0.26),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.profile-action-btn--primary:hover:not(:disabled) {
  border-color: transparent;
  background:
    linear-gradient(135deg, #5b80ec 0%, #a66ad5 100%) padding-box,
    linear-gradient(135deg, rgba(144, 174, 255, 0.88) 0%, rgba(210, 145, 247, 0.82) 100%) border-box;
  box-shadow:
    0 18px 34px rgba(78, 115, 223, 0.24),
    0 14px 28px rgba(0, 0, 0, 0.26);
}

.profile-action-btn--icon:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.09);
}

.profile-action-btn:focus-visible {
  outline: 2px solid rgba(123, 162, 255, 0.55);
  outline-offset: 2px;
}

.profile-action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.profile-dashboard-card,
.profile-achievements-card,
.profile-progression-card,
.profile-insignias-card {
  padding: 20px;
}

.profile-pnl-card {
  margin-top: 12px;
  padding: 16px;
  border-radius: 20px;
  background: linear-gradient(145deg, rgba(16, 20, 31, 0.95), rgba(11, 15, 24, 0.96));
  border: 1px solid rgba(122, 133, 163, 0.18);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 16px 34px rgba(6, 10, 20, 0.3);
}

.profile-pnl-card__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
}

.profile-pnl-label {
  color: #9aa5bd;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.18em;
}

.profile-pnl-value {
  margin-top: 6px;
  font: 800 42px/1 'Montserrat', 'Inter', sans-serif;
  letter-spacing: -0.04em;
  color: #f5f8ff;
}

.profile-pnl-value.value-pos {
  background: linear-gradient(120deg, #d4ffe9 8%, #7bf1c3 54%, #44dba0 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.profile-pnl-value.value-neg {
  background: linear-gradient(120deg, #ffe0e4 8%, #ff9fb0 54%, #f26d83 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.profile-pnl-note {
  color: #8d97ae;
  font-size: 12px;
  white-space: nowrap;
}

.profile-pnl-chart {
  margin-top: 12px;
}

.profile-dashboard-title,
.achievements-title {
  margin: 0;
  color: #f6f8ff;
  font: 800 22px/1.1 'Montserrat', 'Inter', sans-serif;
  letter-spacing: -0.02em;
}

.profile-progression-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  padding: 16px;
  border-radius: 18px;
  background: linear-gradient(145deg, rgba(15, 18, 27, 0.95), rgba(20, 24, 38, 0.95));
  border: 1px solid rgba(122, 133, 163, 0.22);
}

.profile-progression-summary__label {
  color: #8f99af;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.18em;
}

.profile-progression-summary__value {
  margin-top: 6px;
  font: 800 36px/1 'Montserrat', 'Inter', sans-serif;
  color: #f5f8ff;
}

.profile-progression-summary__chip {
  min-width: 112px;
  padding: 10px 12px;
  border-radius: 999px;
  border: 1px solid rgba(122, 133, 163, 0.2);
  background: rgba(255, 255, 255, 0.05);
  color: #dfe6fb;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
}

.profile-progression-bar {
  position: relative;
  height: 12px;
  border-radius: 999px;
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(122, 133, 163, 0.18);
  overflow: hidden;
}

.profile-progression-bar__fill {
  position: absolute;
  inset: 0 auto 0 0;
  border-radius: inherit;
  background: linear-gradient(90deg, #4e73df 0%, #9b59b6 100%);
  box-shadow: 0 0 18px rgba(78, 115, 223, 0.28);
}

.profile-timeline {
  display: grid;
  gap: 12px;
}

.profile-timeline-card {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 16px;
  background: linear-gradient(145deg, rgba(18, 21, 32, 0.95), rgba(14, 17, 25, 0.97));
  border: 1px solid rgba(122, 133, 163, 0.16);
}

.profile-timeline-card.is-current {
  border-color: rgba(78, 115, 223, 0.35);
  box-shadow:
    0 12px 26px rgba(10, 14, 24, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.profile-timeline-card.is-locked {
  opacity: 0.68;
}

.profile-timeline-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.05);
  color: #dbe5ff;
  flex: 0 0 auto;
}

.profile-timeline-card.is-current .profile-timeline-card__icon {
  background: linear-gradient(135deg, rgba(78, 115, 223, 0.18), rgba(155, 89, 182, 0.18));
}

.profile-timeline-card__body {
  min-width: 0;
  flex: 1;
}

.profile-timeline-card__title-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: baseline;
}

.profile-timeline-card__title {
  color: #eef3ff;
  font-size: 14px;
  font-weight: 800;
}

.profile-timeline-card__state {
  color: #9aa3b8;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  white-space: nowrap;
}

.profile-timeline-card__meta {
  margin-top: 4px;
  color: #7f889c;
  font-size: 12px;
  line-height: 1.35;
}

.profile-badge-list {
  display: grid;
  gap: 12px;
}

.profile-badge-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  transition: transform 0.22s ease, box-shadow 0.22s ease, filter 0.22s ease, border-color 0.22s ease;
}

.profile-badge-card::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 18% 18%, rgba(255, 255, 255, 0.14), transparent 44%);
  opacity: 0.7;
  pointer-events: none;
}

.profile-badge-card.is-locked {
  opacity: 0.8;
  filter: saturate(0.82) brightness(0.96);
  background:
    linear-gradient(145deg, rgba(9, 12, 19, 0.98), rgba(13, 16, 24, 0.96)) padding-box,
    linear-gradient(140deg, rgba(89, 100, 142, 0.42), rgba(61, 72, 104, 0.3)) border-box;
  border-color: transparent;
  box-shadow:
    0 14px 28px rgba(0, 0, 0, 0.26),
    inset 0 0 0 1px rgba(92, 103, 142, 0.2);
}

.profile-badge-card.is-locked::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(7, 10, 16, 0.52), rgba(7, 10, 16, 0.46)),
    repeating-linear-gradient(
      -55deg,
      rgba(132, 149, 201, 0.07) 0 8px,
      rgba(25, 30, 46, 0.06) 8px 16px
    );
  pointer-events: none;
}

.profile-badge-card.is-locked > * {
  position: relative;
  z-index: 2;
}

.profile-badge-card.is-unlocked {
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 0 0 1px rgba(122, 145, 255, 0.18),
    0 18px 32px rgba(17, 22, 42, 0.3);
}

.profile-badge-card__orb {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  position: relative;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 10px 22px rgba(7, 10, 18, 0.34);
}

.profile-badge-card__body {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.profile-badge-card__name {
  color: #eef3ff;
  font-size: 14px;
  font-weight: 800;
}

.profile-badge-card__meta {
  color: #8b93a8;
  font-size: 12px;
  line-height: 1.35;
}

.profile-badge-card--discord {
  background:
    radial-gradient(circle at 82% 18%, rgba(88, 101, 242, 0.22), transparent 34%),
    linear-gradient(145deg, rgba(28, 33, 52, 0.97), rgba(15, 18, 28, 0.96));
  border: 1px solid rgba(88, 101, 242, 0.28);
}

.profile-badge-card--beta {
  background:
    radial-gradient(circle at 82% 18%, rgba(181, 106, 255, 0.18), transparent 32%),
    linear-gradient(145deg, rgba(30, 30, 42, 0.97), rgba(16, 18, 27, 0.96));
  border: 1px solid rgba(155, 89, 182, 0.26);
}

.profile-badge-card--tier {
  background: linear-gradient(145deg, rgba(30, 30, 42, 0.95), rgba(16, 18, 27, 0.95));
  border: 1px solid rgba(122, 133, 163, 0.18);
}

.profile-badge-card--discord .profile-badge-card__orb {
  background:
    radial-gradient(circle at 30% 25%, rgba(143, 162, 255, 0.28), transparent 48%),
    linear-gradient(145deg, rgba(37, 43, 72, 0.98), rgba(18, 22, 36, 0.98));
  border: 1px solid rgba(116, 132, 255, 0.28);
}

.profile-badge-card--beta .profile-badge-card__orb {
  background:
    radial-gradient(circle at 30% 25%, rgba(198, 145, 255, 0.28), transparent 48%),
    linear-gradient(145deg, rgba(41, 34, 68, 0.98), rgba(19, 18, 33, 0.98));
  border: 1px solid rgba(171, 117, 241, 0.26);
}

.profile-badge-card.is-locked .profile-badge-card__orb {
  background: linear-gradient(145deg, rgba(26, 31, 45, 0.98), rgba(16, 20, 30, 0.98));
  border: 1px solid rgba(103, 114, 149, 0.2);
}

.profile-badge-card.is-locked .profile-badge-card__orb::after {
  content: '\f023';
  position: absolute;
  right: -4px;
  bottom: -4px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, rgba(17, 20, 31, 0.98), rgba(8, 10, 16, 0.98));
  border: 1px solid rgba(138, 152, 191, 0.22);
  color: #d7def2;
  font-family: 'Font Awesome 5 Free';
  font-size: 7px;
  font-weight: 900;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.32);
}

.profile-badge-card.is-locked .profile-badge-card__name {
  color: #dbe2f4;
}

.profile-badge-card.is-locked .profile-badge-card__meta {
  color: #96a1ba;
}

.profile-badge-card.is-unlocked .profile-badge-card__name {
  text-shadow: 0 0 18px rgba(160, 172, 255, 0.12);
}

.profile-layout--fidelity .pnl-mini-chart {
  padding: 8px 8px 8px 4px;
}

.profile-layout--fidelity .pnl-line-svg {
  height: 128px;
}

.profile-layout--fidelity .pnl-mini-chart__x-axis {
  color: rgba(169, 183, 220, 0.9);
  font-size: 11px;
}

.profile-layout--fidelity .pnl-grid-line {
  stroke: rgba(169, 183, 220, 0.11);
}

.profile-layout--fidelity .pnl-grid-zero {
  stroke: rgba(169, 183, 220, 0.28);
}

@keyframes profileXpPulse {
  0% {
    transform: scale(1);
    opacity: 0.58;
  }
  70% {
    transform: scale(1.7);
    opacity: 0;
  }
  100% {
    transform: scale(1.7);
    opacity: 0;
  }
}

@keyframes avatarRingSpin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .profile-xp-marker::after {
    animation: none !important;
  }
}

@media (max-width: 1199px) {
  .profile-content-grid {
    grid-template-columns: 1fr;
  }

  .profile-hero-grid {
    grid-template-columns: 1fr;
  }

  .profile-hero-identity {
    flex-direction: column;
    text-align: center;
    justify-content: center;
    align-items: center;
  }

  .profile-hero-copy {
    width: 100%;
  }

  .profile-name-row {
    justify-content: center;
  }

  .profile-name-block {
    justify-items: center;
    width: 100%;
  }

  .profile-name-line {
    justify-content: center;
  }

  .profile-discord-pill {
    margin-left: auto;
    margin-right: auto;
  }

  .profile-hero-footer {
    grid-template-columns: 1fr;
  }

  .profile-xp-head {
    flex-direction: column;
    align-items: flex-start;
  }

  .profile-xp-meta {
    margin-left: 0;
    text-align: left;
    white-space: normal;
  }

  .profile-actions {
    justify-content: center;
  }

  .profile-progression-summary {
    flex-direction: column;
    align-items: flex-start;
  }

  .profile-rank-card--hero {
    justify-self: center;
    width: min(100%, 228px);
  }

  .profile-hero-side {
    grid-template-columns: 1fr;
    gap: 10px;
    justify-self: center;
    width: min(100%, 420px);
  }

  .profile-hero-badges {
    width: 100%;
  }

}

@media (max-width: 767px) {
  .profile-page {
    padding: calc(var(--app-header-h) + 12px) var(--app-gutter) 26px;
  }

  .profile-layout--fidelity {
    gap: 16px;
  }

  .profile-content-grid {
    gap: 16px;
  }

  .profile-card {
    padding: 16px;
    border-radius: 20px;
  }

  .profile-hero-card {
    padding: 16px;
  }

  .profile-hero-identity {
    gap: 18px;
  }

  .profile-hero-copy,
  .profile-name-block {
    display: grid;
    justify-items: center;
  }

  .profile-avatar-wrap {
    width: 128px;
    height: 128px;
    padding-top: 12px;
  }

  .avatar-level-chip {
    bottom: -8px;
    font-size: 10px;
  }

  .profile-display-name,
  .profile-display-input {
    font-size: clamp(30px, 9vw, 42px);
  }

  .profile-user-handle {
    font-size: 15px;
  }

  .profile-discord-pill {
    min-height: 36px;
    font-size: 12px;
  }

  .profile-rank-card__title {
    font-size: 20px;
  }

  .profile-hero-footer {
    padding-top: 16px;
    margin-top: 16px;
  }

  .profile-action-btn--primary {
    min-width: 100%;
  }

  .profile-action-btn--icon {
    width: 44px;
  }

  .profile-metrics-grid {
    grid-template-columns: 1fr;
  }

  .profile-pnl-card__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .profile-progression-summary__value {
    font-size: 34px;
  }

  .profile-timeline-card,
  .profile-badge-card {
    padding: 12px;
  }
}
</style>

